import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getKioskCheckInConfig, postKioskCheckIn,
    type KioskCheckInConfig, type KioskCheckInResult,
} from '@/api/client';
import { useBranding } from '@/lib/useBranding';

/**
 * DRIVER CHECK-IN KIOSK — the tablet at the gate. Route: /checkin[?cfg=key]
 *
 * ⚠️ NO LOGIN, BY DESIGN (arch §1D). A driver has no account and never will.
 * The gate is the facility's egress IP, checked server-side; this page sends no
 * facility and could not claim one if it tried. If the tablet is off-network it
 * gets a locked screen, exactly like the labour clock.
 *
 * ⭐ BILINGUAL, BECAUSE THE LABOUR CLOCK IS. laborclock.symbia.com already asks
 * "Which truck? / ¿Cuál camión?" and the same population of people stands in
 * front of both. A driver check-in in English only would be the odd one out on
 * the same site.
 *
 * ⚠️ EVERY FIELD IS OPTIONAL EXCEPT THE PHONE STEP'S OWN GATE. A driver who
 * cannot produce a container number must still be able to check in -- an
 * unmatched arrival is a truck a scheduler resolves, whereas a refused arrival
 * is a truck nobody knows is here. The server applies the same rule.
 *
 * ⚠️ NO DRIVER'S-LICENCE CAPTURE. dock.CheckInConfig carries a DlCaptureMode and
 * this page ignores it, because no upload path exists yet -- not in this page,
 * not in the endpoint, not in blob storage. Setting the mode to 'required'
 * therefore enforces NOTHING today. Rendering a fake photo step would be worse
 * than the gap; when DL capture is built it needs its own security review,
 * because that one is about PII rather than junk data.
 */

const IDLE_FALLBACK_SECONDS = 90;
/** How long a finished check-in stays on screen before the kiosk clears itself. */
const DONE_RESET_SECONDS = 5;

/**
 * ⚠️ 'found' AND 'details' ARE GONE (2026-08-25). The kiosk is phone → sign-offs → done;
 * the driver never picks a load and never fills in a form. See the note where those
 * screens used to render.
 */
/**
 * ---- ⭐⭐ 'vehicle' ADDED 2026-09-22 (#73) --------------------------------
 *
 * > Pete: *"we need to get the trailer number or container number screen in the
 * > driver check-in on this build"*
 *
 * ⛔⛔ THIS PARTLY REVERSES A DELIBERATE REMOVAL, AND THAT IS WORTH SAYING OUT
 * LOUD RATHER THAN SLIPPING IN. The long form was taken off this kiosk because
 * *"they are not going to look at the paperwork and confirm the info consistently
 * and accurately"* — the note is still a few hundred lines below. The judgement
 * stands for NAME, CARRIER, PALLETS and the free paperwork number, all of which
 * stay off.
 * ⭐ TRAILER AND CONTAINER ARE DIFFERENT AND THAT IS WHY THEY COME BACK: they are
 * painted on the box in front of the driver, not read off paperwork he has to
 * find. And the trailer line is blank on the BOL, so the gate is the only place
 * the number is available at all.
 *
 * ⚠️ IT IS SKIPPED ENTIRELY when both config flags are off, in BOTH directions —
 * forward from 'phone' and backward from 'safety'. A step that renders with no
 * fields is a tap that costs a driver time and teaches nothing.
 */
/**
 * ---- ⭐⭐ THE SUCCESSION (Pete, 2026-09-23) ------------------------------
 *
 * > *"Lets add the exact same screen but asking for PO number, Reference Number
 * > or BOL … the confirmation screen goes after the 2 screens to confirm both,
 * > then to the you are checked in screen … last screen in that succession
 * > would be number of pallets."*
 *
 *   phone -> vehicle -> paperwork -> pallets -> review -> [safety] -> done
 *
 * ⚠️ EVERY MIDDLE STEP IS OPTIONAL PER SITE, and the order lives in exactly one
 * place (`CHAIN` / `nextAfter` below) rather than being re-stated by each
 * screen's Next handler. The old two-step version already had the order written
 * out twice and it was already drifting.
 *   ** A SEQUENCE SPELLED OUT AT EVERY STEP IS A SEQUENCE THAT DISAGREES WITH
 *      ITSELF THE FIRST TIME SOMEBODY INSERTS A SCREEN. **
 */
type Step = 'phone' | 'vehicle' | 'paperwork' | 'pallets' | 'review' | 'safety' | 'done';

/**
 * Group a run of digits for DISPLAY only. Never used for matching or storage --
 * the server gets bare digits (see submit()), and matching normalises to the
 * last ten anyway.
 *
 * ⚠️ FORMATS PARTIAL INPUT. A driver watches the number build as they press
 * keys, so this has to look right at 3, 6 and 10 digits, not only when complete.
 * The old version formatted ONLY an exact 10 and fell back to an unbroken run
 * otherwise -- which is why a number typed with a country code showed as one
 * long string with no separators at all.
 */
function groupTen(d: string): string {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

export function formatPhoneDigits(d: string): string {
    if (!d) return '';
    // ⭐ A LEADING 1 IS ALWAYS A COUNTRY CODE, AND IS SPLIT OFF IMMEDIATELY.
    //
    // Not only once eleven digits have been typed. NANP area codes never begin
    // with 1, so a leading 1 cannot be anything else -- which means we can be
    // certain about it from the first keypress instead of guessing.
    //
    // Waiting was the bug: '1312952048' rendered as '131-295-2048', silently
    // folding the country code into the area code, so the number looked wrong
    // and one digit short right up until the eleventh press.
    if (d[0] === '1') return d.length === 1 ? '1' : `1-${groupTen(d.slice(1))}`;
    // Any other country code has no fixed length, so it can only be inferred
    // once there are more digits than a local number needs.
    if (d.length > 10) return `${d.slice(0, d.length - 10)}-${groupTen(d.slice(-10))}`;
    return groupTen(d);
}

/**
 * Everything shown to a driver, in one place.
 *
 * ⚠️ EACH ENTRY STILL CARRIES ITS SPANISH, and nothing here was deleted. Only
 * the RENDERING stopped - see `KIOSK_SHOW_SPANISH`. Translated copy is work
 * somebody did, and a flag that turns it off does not need it destroyed too.
 */
const T = {
    title:      ['Driver check-in', 'Registro de conductor'],
    phoneQ:     ['Your cell number', 'Su número de celular'],
    phoneHelp:  ['We use it to find your appointment', 'Lo usamos para encontrar su cita'],
    next:       ['Next', 'Siguiente'],
    back:       ['Back', 'Atrás'],
    cancel:     ['Cancel', 'Cancelar'],
    name:       ['Your name', 'Su nombre'],
    carrier:    ['Carrier', 'Transportista'],
    client:     ['Client', 'Cliente'],
    pickLang:   ['Choose one', 'Elija uno'],
    paperQ:     ['Any number from your paperwork',
                 'Cualquier número de sus documentos'],
    paperHelp:  ['Container, trailer, PO, BOL or reference — whichever you have',
                 'Contenedor, remolque, OC, BOL o referencia — el que tenga'],
    search:     ['Search', 'Buscar'],
    searching:  ['Searching…', 'Buscando…'],
    noHit:      ['We could not find that number. Try another, or skip and see the office.',
                 'No encontramos ese número. Pruebe otro, u omita y vaya a la oficina.'],
    skip:       ['Skip', 'Omitir'],
    /* ⭐ THE STEP'S OWN WORDS (#73). ⚠️ `container`, `trailer` and `trailerHelp`
       below were ALREADY HERE — left behind when the long form was removed — so the
       Spanish did not have to be written, only used. */
    /* ⛔⛤ ~~vehicleQ: ['Your trailer', 'Su remolque']~~ — struck 2026-09-23.
       The screen below it no longer asks only about a trailer; it takes ONE value
       that may be a container, a trailer or a plate, and a heading naming one of
       the three tells the other two drivers they are on the wrong screen.
         ** A HEADING IS A PROMISE ABOUT WHAT THE SCREEN WANTS. ** */
    vehicleQ:   ['Your trailer or container', 'Su remolque o contenedor'],
    /**
     * ---- ⭐⭐ WHY WE ARE ASKING, BEFORE WE ASK (Pete, 2026-09-22) ------------
     *
     * > *"A little blurb to the driver above the question — this will be used to
     * > manage your position in the yard and give you the correct position for a
     * > dock door"*
     *
     * ⭐ IT IS WRITTEN AS WHAT THE DRIVER GETS, NOT WHAT WE GET. "Used to manage
     * your position in the yard" is true and is the yard's sentence; "so we can
     * find you and send you to the right door" is the same fact from the cab, and
     * a man standing at a gate at 6am is answering the second one.
     *   ** A QUESTION A KIOSK CANNOT JUSTIFY IN ONE LINE IS A QUESTION PEOPLE
     *      ANSWER BADLY ON PURPOSE. **
     *
     * ⚠️ TWO LINES, NOT ONE. The reason and the "both optional" hint are different
     * kinds of statement — one earns the answer, the other removes the pressure —
     * and a driver reading a single long sentence takes neither.
     * ⛔ NO PROMISE THAT IS NOT KEPT. It does not say we will text him the door or
     * hold his place in a queue; it says what the number is used for. A gate screen
     * that over-promises is one the desk spends the morning apologising for.
     */
    vehicleWhy: ['We use this to find you in the yard and send you to the right dock door.',
                 'Usamos esto para ubicarle en el patio y enviarle a la puerta correcta.'],
    /**
     * ⛔⛤ ~~'Type what is painted on the box. Both are optional.'~~ — struck
     * 2026-09-23. They are not optional any more where the site collects them,
     * and a hint that says they are is worse than none: a driver reads it, taps
     * Next, and meets a refusal that contradicts the line above the field.
     *   ** A HINT THAT DESCRIBES THE OLD RULE IS NOT STALE COPY, IT IS A
     *      CONTRADICTION THE USER MEETS ONE TAP LATER. **
     *
     * > Pete, 2026-09-23: *"There should be a blurb at the time saying Trailer or
     * > container number is required for dock door assignment and will position
     * > you in the queue for assignment."*
     *
     * ⚠️ IT NAMES ALL THREE FIELDS, because the screen offers all three — a
     * sentence naming two of them reads as a bug on the plate.
     */
    vehicleHelp:['A trailer, container or plate number is required for your dock door, and sets your place in the queue.',
                 'Se requiere un número de remolque, contenedor o placa para su puerta, y define su lugar en la fila.'],
    /** ⚠️ Shown only when he taps Next with nothing usable. The SERVER sends its
     *  own bilingual refusal too; this one exists so the tablet can answer
     *  without a round trip. */
    vehicleNeed:['Enter one of these to continue.',
                 'Ingrese uno de estos para continuar.'],
    /* ⛔⛤ ~~cancel: ['Cancel', 'Cancelar']~~ — added here 2026-09-23 and struck
       the same hour: the key already exists at the top of this table with
       identical text, and `tsc` refused the duplicate (TS1117).
         ** A STRINGS TABLE IS A NAMESPACE. CHECK IT BEFORE ADDING TO IT. ** */
    container:  ['Container number', 'Número de contenedor'],
    trailer:    ['Trailer number', 'Número de remolque'],
    trailerHelp:['The number painted on the trailer', 'El número pintado en el remolque'],
    plate:      ['Trailer license plate', 'Placa del remolque'],
    /**
     * ---- ⭐⭐ ONE BOX, THEN "WHICH IS IT?" (Pete, 2026-09-23) ---------------
     *
     * > *"Can't we have one text box here and ask enter your container number or
     * > license plate number or trailer number below: then below the text box
     * > have 3 buttons, each one saying something like this is my container
     * > number i entered, that will automatically push him to the next screen."*
     *
     * ⭐ HE TYPES FIRST AND CLASSIFIES SECOND, which is the right way round: a
     * driver reads what is painted on the box and then says what it is. Asking
     * him to choose a KIND before typing makes him answer a question about his
     * own number while he is still looking for it.
     *
     * ⛔ EACH BUTTON IS ALSO THE NEXT BUTTON. One action, not two.
     */
    vehicleAsk: ['Enter your container, trailer or license plate number',
                 'Ingrese su n\u00famero de contenedor, remolque o placa'],
    vehicleWhich: ['Now tell us which one you typed',
                   'Ahora d\u00edganos cu\u00e1l escribi\u00f3'],
    isContainer: ['This is my CONTAINER number', 'Este es mi n\u00famero de CONTENEDOR'],
    isTrailer:   ['This is my TRAILER number', 'Este es mi n\u00famero de REMOLQUE'],
    isPlate:     ['This is my LICENSE PLATE', 'Esta es mi PLACA'],
    /**
     * ---- ⭐⭐ THE NOUNS, FOR THE REVIEW SCREEN (Pete, 2026-09-23) -----------
     *
     * > *"They also need to carry over what the select in the title - its either
     * > trailer number, container number or license number, not all three."*
     *
     * ⛔ SEPARATE FROM `isContainer` ABOVE, WHICH IS A SENTENCE. "This is my
     * CONTAINER number" is what a driver PRESSES; "Container number" is what a
     * receipt CALLS it. Reusing the button's wording on the review row would read
     * as an instruction to press something that is not there.
     *   ** A LABEL AND A BUTTON ARE DIFFERENT PARTS OF SPEECH. **
     */
    nounContainer: ['Container number', 'Numero de contenedor'],
    nounTrailer:   ['Trailer number', 'Numero de remolque'],
    nounPlate:     ['License plate', 'Placa'],
    nounPo:        ['PO number', 'Numero de orden'],
    nounBol:       ['BOL number', 'Numero de BOL'],
    nounRef:       ['Reference number', 'Numero de referencia'],
    /* ⭐ The review row's own control. ⚠️ It edits the VALUE only - the TYPE was
       chosen on the screen behind it, and Back is how that changes. */
    edit:          ['Edit', 'Editar'],
    /**
     * ⚠️ THE ONE BOX HOLDS 50, BECAUSE A TRAILER NUMBER DOES. `TrailerPlate` is
     * `nvarchar(20)` and the route's zod says so, so the only way a single box can
     * serve all three is to accept the widest and refuse the narrow one HERE —
     * before a round trip that would come back as a generic failure.
     *   ** ONE BOX FOR THREE COLUMNS INHERITS THE WIDEST LIMIT AND OWES THE
     *      NARROWEST AN EXPLANATION. **
     */
    plateLong:  ['A license plate is at most 20 characters.',
                 'Una placa tiene un m\u00e1ximo de 20 caracteres.'],
    /**
     * ⭐ THE DONE SCREEN'S RECEIPT. See `vehicleRecorded` on the result — the
     * SERVER decides whether there is anything to say here, because it is the only
     * side that knows the bypass code. ⚠️ Each line is a whole sentence rather
     * than a label glued to a value: `E` renders English and Spanish as two lines,
     * so "Container" + ": " + value would interleave the two languages.
     */
    recContainer:['Container number recorded', 'N\u00famero de contenedor registrado'],
    recTrailer:  ['Trailer number recorded', 'N\u00famero de remolque registrado'],
    recPlate:    ['License plate recorded', 'Placa registrada'],
    plateHelp:  ['The plate on the trailer, not the truck', 'La placa del remolque, no del camión'],
    po:         ['PO number', 'Número de orden'],
    reference:  ['Symbia reference', 'Referencia Symbia'],
    /**
     * ---- ⭐⭐ THE PAPERWORK SCREEN (Pete, 2026-09-23) ---------------------
     *
     * > *"the exact same screen but asking for PO number, Reference Number or
     * > BOL. Same layout, same buttons"*
     *
     * ⚠️ DELIBERATELY THE SAME SHAPE AS THE VEHICLE SCREEN, down to the wording
     * pattern. A driver who has just answered one of these knows what the second
     * one wants without reading it twice, and that is worth more at a gate than
     * variety.
     */
    paperQ2:    ['Your paperwork', 'Sus documentos'],
    paperWhy2:  ['We use this to match you to your booking and your load.',
                 'Usamos esto para relacionarle con su cita y su carga.'],
    paperHelp2: ['A PO, BOL or reference number is required so we can find your load.',
                 'Se requiere un n\u00famero de orden, BOL o referencia para encontrar su carga.'],
    paperAsk:   ['Enter your PO number, BOL number or reference number',
                 'Ingrese su n\u00famero de orden, BOL o referencia'],
    isPo:       ['This is my PO number', 'Este es mi n\u00famero de ORDEN'],
    isBol:      ['This is my BOL number', 'Este es mi n\u00famero de BOL'],
    isRef:      ['This is my REFERENCE number', 'Este es mi n\u00famero de REFERENCIA'],
    paperNeed:  ['Enter one of these to continue.', 'Ingrese uno de estos para continuar.'],
    /**
     * ---- ⭐⭐ THE PALLET SCREEN --------------------------------------------
     *
     * > *"Very basic, numeric, and just like the others stored with the driver."*
     *
     * ⛔ THE ONLY SKIPPABLE SCREEN IN THE SUCCESSION, and that is a decision, not
     * an oversight. A driver at a gate often does not know his count until the
     * doors are open; refusing him over it strands a truck on the yard to protect
     * a number he would have had to invent anyway.
     *   ** A FIELD MARKED REQUIRED IS A CLAIM THAT THE ANSWER ALWAYS EXISTS. **
     *
     * ⛔⛤ AND `palletQ` / `palletHelp` ARE NOT DECLARED HERE. Both already exist
     * further down this table — leftovers from the long form that was removed —
     * and re-declaring them is TS1117, which is exactly how the `cancel` key was
     * added and struck within the hour on 2026-09-23.
     *   ** A STRINGS TABLE IS A NAMESPACE. CHECK IT BEFORE ADDING TO IT. **
     * ⚠️ The existing `palletHelp` says *"On the truck right now"*, which is the
     * right sub-line for this screen, so it is reused as-is and the skip wording
     * gets a name of its own.
     */
    palletWhy:  ['We use this to plan the door and the crew.',
                 'Usamos esto para planificar la puerta y el personal.'],
    palletSkip: ['If you do not know yet, tap Skip.',
                 'Si a\u00fan no lo sabe, toque Omitir.'],
    palletAsk:  ['Number of pallets', 'N\u00famero de tarimas'],
    /**
     * ---- ⭐⭐ THE REVIEW SCREEN (Pete: *"the confirmation screen goes after
     * the 2 screens to confirm both"*) --------------------------------------
     *
     * ⚠️ IT SHOWS WHAT HE TYPED, INCLUDING THE BYPASS CODE. Asked and answered
     * 2026-09-23: the tablet is never told the code (that payload is
     * unauthenticated, arch §1D) so it cannot suppress a line it cannot
     * recognise, and Pete chose showing it over publishing the code to the
     * tablet. The DONE screen still reports what the SERVER stored, which is
     * where a bypass correctly shows nothing.
     *   ** THE REVIEW SCREEN SHOWS A CLAIM. THE DONE SCREEN SHOWS A FACT. **
     */
    reviewQ:    ['Please check this is right', 'Por favor revise que est\u00e9 correcto'],
    reviewWhy:  ['Tap Back to change anything.', 'Toque Atr\u00e1s para cambiar algo.'],
    revVehicle: ['Trailer / container', 'Remolque / contenedor'],
    revPaper:   ['Paperwork', 'Documentos'],
    revPallets: ['Pallets', 'Tarimas'],
    notGiven:   ['Not given', 'No indicado'],
    confirmIn:  ['Yes, check me in', 'S\u00ed, reg\u00edstreme'],
    language:   ['Preferred language', 'Idioma preferido'],
    optional:   ['optional', 'opcional'],
    /**
     * ⛔ THE BRACKETS BELONG TO THE PHRASE, NOT TO THE LAYOUT — and this is a bug fix.
     *
     * ⚠️ It was written as `(<E k="optional" />)`, which renders as THREE LINES:
     *
     *     (optional
     *     opcional
     *     )
     *
     * because `E` stacks the Spanish underneath in a BLOCK span. A bracket opened on
     * one line and closed two lines later is not a typo a driver forgives — it reads
     * as a broken screen, on the one surface where we cannot answer questions.
     *
     * ⭐ SO EACH LANGUAGE CARRIES ITS OWN BRACKETS and each line is complete on its own.
     * ⛔ Nothing that wraps `E` may put punctuation outside it; the same trap is waiting
     * for the next person who writes `<E/>:` or `"<E/>"`.
     */
    optionalParen: ['(optional)', '(opcional)'],
    safetyQ:    ['Please read and confirm', 'Por favor lea y confirme'],
    agree:      ['I confirm', 'Confirmo'],
    checkIn:    ['Check in', 'Registrarse'],
    working:    ['Checking in…', 'Registrando…'],
    doneT:      ['You are checked in', 'Está registrado'],
    arrival:    ['Arrival number', 'Número de llegada'],
    truck:      ['Truck number', 'Número de camión'],
    office:     ['Please go to the check-in desk', 'Por favor vaya al mostrador de registro'],
    /**
     * ⚠️ REWORDED 2026-08-25. It used to read "We could not match you to an appointment",
     * which was true of the old flow and is a LIE about this one: nothing failed. The
     * desk does the matching now, on purpose, after it has seen his licence.
     * ⛔ A driver told we "could not find" him goes to the counter expecting a problem.
     */
    officeWhy:  ['The desk will finish your check-in and give you your door',
                 'La oficina completará su registro y le dará su puerta'],
    already:    ['You are already checked in', 'Ya está registrado'],
    againIn:    ['Checked in', 'Registrado'],
    minsAgo:    ['minutes ago', 'hace minutos'],
    locked:     ['This tablet is not registered', 'Esta tableta no está registrada'],
    lockedWhy:  ['Ask the office to register this device',
                 'Pida a la oficina que registre este dispositivo'],
    busy:       ['Too many check-ins at once', 'Demasiados registros a la vez'],
    needSome:   ['Enter your phone number or your name',
                 'Ingrese su número de teléfono o su nombre'],
    reread:     ['The safety notice changed. Please read it again.',
                 'El aviso de seguridad cambió. Por favor léalo de nuevo.'],
    another:    ['Check in another driver', 'Registrar otro conductor'],
    foundOne:   ['We found your appointment', 'Encontramos su cita'],
    foundMany:  ['Which load is yours?', '¿Cuál es su carga?'],
    yesMine:    ['Yes, that is mine', 'Sí, es la mía'],
    notMine:    ['Not mine', 'No es la mía'],
    looking:    ['Looking…', 'Buscando…'],
    apptNo:     ['Appointment', 'Cita'],
    doorLbl:    ['Door', 'Puerta'],
    noDoorYet:  ['Door not assigned yet', 'Puerta aún no asignada'],
    willTell:   ['The scheduler will contact you when a door is assigned and when it is your turn to pull in',
                 'El programador le avisará cuando se asigne una puerta y cuando sea su turno de entrar'],
    poLbl:      ['PO', 'OC'],
    dirQ:       ['Are you delivering or picking up?', '¿Está entregando o recogiendo?'],
    delivering: ['Delivering', 'Entregando'],
    pickingUp:  ['Picking up', 'Recogiendo'],
    notPlace:   ['This is your check-in number, not your place in line',
                 'Este es su número de registro, no su lugar en la fila'],
    forClient:  ['for', 'para'],
    contQ:      ['Container or trailer number', 'Número de contenedor o remolque'],
    palletQ:    ['How many pallets?', '¿Cuántas tarimas?'],
    palletHelp: ['On the truck right now', 'En el camión ahora mismo'],
    addLater:   ['Skip', 'Omitir'],
    resetting:  ['Returning to the start…', 'Volviendo al inicio…'],
    refLbl:     ['Reference', 'Referencia'],
    contLbl:    ['Container', 'Contenedor'],
    driverLbl:  ['Driver', 'Conductor'],
    clear:      ['Clear the number', 'Borrar el número'],
    backspace:  ['Delete one digit', 'Borrar un dígito'],
} as const;

/**
 * Preferred language, for the driver to pick.
 *
 * ⚠️ ENGLISH AND SPANISH ARE PINNED FIRST, deliberately out of alphabetical
 * order, because between them they cover almost every driver at these gates and
 * a driver should not scroll to find his own. Everything after them is
 * alphabetical so the rest is predictable to scan.
 *
 * ⚠️ Hardcoded for now, which sits awkwardly against the standing "everything in
 * a config screen" rule. It is here rather than in dock.CheckInConfig because
 * the list is the same at every facility and changes about never -- but if a
 * site ever needs its own, this is the thing to move.
 */
/**
 * ⚠️ KEPT THOUGH NOTHING RENDERS IT TODAY — the picker was removed 2026-08-25 (see the
 * note at the old field). This list is the vocabulary a stored `Language` value came
 * from, and deleting it would leave historic rows holding strings with no defining set.
 * It is the list to reuse when language moves onto the driver's identity.
 */
export const LANGUAGES = [
    'English', 'Spanish',
    'Amharic', 'Arabic', 'Bengali', 'Bosnian', 'Burmese', 'Chinese (Cantonese)',
    'Chinese (Mandarin)', 'Dari', 'Farsi', 'French', 'German', 'Gujarati',
    'Haitian Creole', 'Hindi', 'Hmong', 'Italian', 'Japanese', 'Karen', 'Korean',
    'Nepali', 'Oromo', 'Pashto', 'Polish', 'Portuguese', 'Punjabi', 'Romanian',
    'Russian', 'Serbian', 'Somali', 'Swahili', 'Tagalog', 'Tigrinya', 'Turkish',
    'Ukrainian', 'Urdu', 'Vietnamese', 'Other',
] as const;

/**
 * ============================================================================
 * ⛔⛔ THE KIOSK IS ENGLISH ONLY (Pete, 2026-09-23)
 * ============================================================================
 *
 * > *"this should not have spanish on it, only engish as we have a separate
 * > config for that."*
 *
 * ---- ⚠️ WHY THIS IS A CONSTANT AND NOT A CONFIG READ ---------------------
 *
 * ⛔ THERE IS NO CONFIG THAT MEANS THIS TODAY. `collectLanguage` is the only
 * language flag on `CheckInConfig`, and it means *"ask the driver what he
 * speaks"* - a question this page stopped asking on 2026-08-25 and which is
 * booked to come back on the driver IDENTITY, not the visit. Pointing this at
 * it would give one column two meanings, and the second one would be found by
 * whoever turns the first one back on.
 *   ** A FLAG REUSED FOR A SECOND PURPOSE IS A BUG WITH A SCHEDULED DELIVERY
 *      DATE. **
 *
 * ⭐ So: a named constant, false, in one place. When a real per-site language
 * setting exists this becomes a prop off `config` and every call site below is
 * already routed through it.
 *
 * ⛔⛔ AND IT IS NOT COPIED INTO THE BACKEND. `driverCheckIn.ts` returns the
 * English half of its refusals because THE KIOSK IS ENGLISH, not because it
 * holds a second copy of this constant - `web/` and `src/` cannot import from
 * each other, and a duplicated boolean is two things that drift.
 *     ** A CONSTANT COPIED TO KEEP TWO MODULES INDEPENDENT HAS MADE THEM
 *        DEPENDENT ON NOBODY NOTICING. **
 */
export const KIOSK_SHOW_SPANISH = false;

/**
 * ⭐ THE ONE COMPOSER for strings that cannot be JSX - error text, aria-labels.
 *
 * ⚠️ IT EXISTS BECAUSE THERE WERE FIVE CALL SITES, NOT ONE. Three `setError`
 * calls and two keypad `aria-label`s each built "English / Spanish" by hand,
 * and a flag that only reached `E` would have left a driver an English screen
 * with bilingual error messages and a bilingual screen reader.
 *   ** A RULE APPLIED AT THE PLACE YOU HAPPENED TO LOOK IS NOT A RULE. **
 */
function both(k: keyof typeof T): string {
    return KIOSK_SHOW_SPANISH ? T[k][0] + ' / ' + T[k][1] : T[k][0];
}

/** `E` renders one string, and the Spanish beneath it only when it is on. */
function E({ k, className }: { k: keyof typeof T; className?: string }) {
    return (
        <span className={className}>
            {T[k][0]}
            {KIOSK_SHOW_SPANISH && <span className="es">{T[k][1]}</span>}
        </span>
    );
}

export default function DriverCheckInPage() {
    const cfgKey = new URLSearchParams(window.location.search).get('cfg');

    const [config, setConfig] = useState<KioskCheckInConfig | null>(null);
    const [locked, setLocked] = useState(false);
    const [step, setStep] = useState<Step>('phone');
    /**
     * ⛔ `matches` / `chosen` ARE GONE. The kiosk no longer looks a driver up or offers
     * him loads to pick from — the scheduler associates the arrival after scanning his
     * licence (2026-08-25). `looking` survives only as the submit button's spinner.
     */
    const [looking] = useState(false);
    /**
     * ONE FIELD FOR EVERY NUMBER ON THE PAPERWORK. It replaced separate
     * container / trailer / PO / reference boxes: a driver holding a bill of
     * lading should not have to work out which of our four boxes his number
     * belongs in, and getting it wrong meant no match.
     */
    /**
     * ⭐ REUSED, NOT REPLACED. `paperwork` has been declared here since the long
     * form was removed and NOTHING has written it since — it was set only by
     * `reset()`, so it has been `''` on every submission. It is now the paperwork
     * screen's single box, which is what the name always meant.
     *   ** A STATE NOBODY WRITES IS NOT A SPARE PART. IT IS A FIELD THAT LOST ITS
     *      SCREEN, AND GIVING IT A NEW ONE BEATS DECLARING ITS TWIN. **
     */
    const [paperwork, setPaperwork] = useState('');
    /* ⚠️ WHAT THE CLASSIFY BUTTON DECIDED IT WAS. Exactly the vehicle screen's
       arrangement: one box the driver fills, three fields only a button writes. */
    const [poNo, setPoNo] = useState('');
    const [bolNo, setBolNo] = useState('');
    const [refNo, setRefNo] = useState('');
    /**
     * ⭐ #146/#168 — TWO FIELDS, BECAUSE THEY ARE TWO FACTS. Pete, 2026-08-24: "The
     * Trailer License Plate and Trailer ID are two different fields." The number is
     * the carrier's own asset id painted on the box; the plate is the registration a
     * yard camera reads and a police report cites.
     *
     * ⛔ AND NEITHER CAN COME FROM THE PAPERWORK. The trailer line is blank on the
     * BOL — confirmed again on the Landstar/Hellman document Pete sent the same day,
     * where the trailer is empty and the truck number is handwritten. Both facts
     * arrive with the DRIVER, so the gate is the only place they can be captured.
     */
    /**
     * ⚠️ STILL SENT, NO LONGER ASKED FOR HERE. Both facts arrive with the DRIVER — the
     * trailer line is blank on the BOL — so the gate was the only place to capture them
     * while the gate had a form. It does not any more, so these stay empty and the DESK
     * captures them, from the trailer itself rather than from the driver's memory.
     * ⛔ Kept rather than deleted: `submit()` still sends them, and the server still
     * stores them, so the day a capture point returns there is nothing to rebuild.
     */
    /* ⭐ THE SETTER IS BACK. ⛔⛤ It was ~~const [trailerNo] = useState('')~~ — a
       const that could never be anything but '', so `submit()`'s
       `trailerNumber: trailerNo.trim().toUpperCase() || undefined` was ALWAYS
       undefined. The line read as a working capture and sent nothing, every time.
         ** A SEND LINE PROVES WHAT IS SENT ONLY IF SOMETHING CAN SET THE VALUE.
            READ THE STATE, NOT THE PAYLOAD. ** */
    const [trailerNo, setTrailerNo] = useState('');
    /* ⚠️ PLATE STAYS DEAD, ON PURPOSE. Pete asked for trailer and container; the
       plate is a third question at a gate, and this step is already two. The state
       and its send line stay so nothing has to be rebuilt if it is ever asked for. */
    /**
     * ⛔⛤ ~~const [trailerPlate] = useState('')~~ — THE SAME BUG AS `trailerNo`,
     * ONE LINE BELOW IT, FOUND 2026-09-23.
     *
     * ⚠️ `TrailerPlate` has existed since migration 161 (2026-08-24) and the kiosk
     * has NEVER SENT A VALUE: `submit()` reads
     * `trailerPlate.trim().toUpperCase() || undefined`, which with no setter is
     * always `undefined`. `trailerNo` had the identical fault and was fixed on
     * 2026-09-22 — this one was one line away and was not noticed.
     *   ** WHEN YOU FIND A FIELD WITH NO SETTER, LOOK AT ITS NEIGHBOURS — THE
     *      SAME HAND WROTE THEM. **
     *   ** A SEND LINE PROVES WHAT IS SENT ONLY IF SOMETHING CAN SET THE VALUE.
     *      READ THE STATE, NOT THE PAYLOAD. **
     */
    const [trailerPlate, setTrailerPlate] = useState('');
    /**
     * ⭐ A DEDICATED CONTAINER FIELD (#73).
     *
     * ⚠️ IT DOES NOT REPLACE THE ISO 6346 GUESS, it outranks it — see `submit()`.
     * The guess reads a container id out of the free paperwork field, and that
     * field is not rendered today, so in practice this becomes the only source.
     * ⛔ TWO SOURCES FOR ONE COLUMN NEEDS A STATED WINNER or one of them is
     * silently dropped on the day both are filled.
     */
    const [containerNo, setContainerNo] = useState('');
    /**
     * ---- ⛔⛔⤴ WHICH REVIEW ROW IS BEING EDITED - AND WHY IT IS UP HERE ------
     *
     * ⭐ One of 'vehicle' | 'paper' | 'pallets', or null. ONE AT A TIME: three open
     * boxes on a confirmation screen is not a confirmation screen.
     *
     * ⛔⛔⛤ IT WAS DECLARED BESIDE `classifyVehicle`, ~650 LINES BELOW THE EARLY
     * RETURNS, AND IT TOOK THE KIOSK DOWN. 2026-09-23: Pete, *"driver checkin
     * screen is just white now. Not even the first page showing."*
     *
     * `config` starts null, so the first render returns at `if (!config)` having
     * called N hooks. The fetch resolves, the next render walks past that return
     * and reaches this line - N+1 hooks. React throws #310 and UNMOUNTS THE WHOLE
     * TREE, so the symptom is a completely blank page with the cause only in the
     * console.
     *
     * ⚠️ THE FILE ALREADY SAID SO, IN CAPITALS, in the `useBranding` note above:
     * *"ANY hook added to this component must go above line ~470. Below the early
     * returns is not 'later in the file', it is a different number of hooks."*
     * That note exists because this exact fault shipped once before, in
     * `PaperworkSliver.tsx`, and was still being cleaned up the same week.
     *   ** A WARNING YOU HAVE READ IS NOT A WARNING YOU HAVE APPLIED. THE PLACE IT
     *      APPLIES IS WHEREVER YOU ARE TYPING, NOT WHERE IT IS WRITTEN. **
     *
     * ⛔ A HOOK IS NOT "STATE FOR THE REVIEW SCREEN". It is state for the
     * COMPONENT, and the component is one hook list from its first render to its
     * last. Putting it next to the code that uses it is the instinct that breaks it.
     */
    const [editing, setEditing] = useState<'vehicle' | 'paper' | 'pallets' | null>(null);
    /** Only meaningful for a walk-up; a matched appointment supplies its own. */
    const [direction, setDirection] = useState<'INBOUND' | 'OUTBOUND'>('INBOUND');
    /**
     * What is ON the truck — which is not always what was booked.
     *
     * ⚠️ '' AND '0' ARE DIFFERENT ANSWERS. Blank means he was not asked or did
     * not answer; zero means an empty trailer, which is a real thing that
     * arrives. Kept as a string so the field can hold "not answered" at all.
     */
    const [pallets, setPallets] = useState('');
    const [busyMsg, setBusyMsg] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    /**
     * ⭐⭐ ONE BOX. The three separate fields are gone from the SCREEN, not from
     * the payload — `classifyVehicle` writes the typed value into whichever of the
     * three the driver names, so `submit()`, the route, the columns and every
     * consumer downstream are untouched.
     *   ** CHANGING WHICH QUESTION A SCREEN ASKS SHOULD NOT CHANGE WHAT THE
     *      RECORD MEANS. **
     */
    const [vehicleValue, setVehicleValue] = useState('');
    const [result, setResult] = useState<KioskCheckInResult | null>(null);

    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [carrier, setCarrier] = useState('');
    const [agreed, setAgreed] = useState(false);

    const loadConfig = useCallback(async () => {
        try {
            const { config: c } = await getKioskCheckInConfig(cfgKey);
            setConfig(c);
            setLocked(false);
        } catch (err) {
            // 403 means this device's IP is not registered. Anything else is
            // also unusable from a gate, so both show the locked screen rather
            // than a stack trace a driver cannot act on.
            setLocked(true);
            void err;
        }
    }, [cfgKey]);

    useEffect(() => { void loadConfig(); }, [loadConfig]);

    const reset = useCallback(() => {
        setStep('phone');
        setPaperwork(''); setDirection('INBOUND'); setPallets('');
        /* ⚠️ THE THREE CLASSIFIED PAPERWORK FIELDS TOO. Same reason as the
           vehicle trio below: a shared tablet must not hand the next driver the
           last one's PO number. */
        setPoNo(''); setBolNo(''); setRefNo('');
        /* ⛔ A SHARED TABLET. The next driver must not find the last one's trailer
           number pre-filled — he will tap Next past it. */
        /* ⚠️ ALL THREE. The plate joined this line on 2026-09-23; leaving it out
           would hand the next driver the last one's registration. */
        /* ⚠️ AND THE BOX. It is what the driver actually typed; the three above
           are only ever what a classify button copied out of it. */
        setTrailerNo(''); setContainerNo(''); setTrailerPlate(''); setVehicleValue('');
        setPhone(''); setName(''); setCarrier('');
        setAgreed(false); setResult(null); setError(null); setBusyMsg(null);
    }, []);

    /**
     * ⚠️ A SHARED TABLET IS A PII SURFACE. The previous driver's name and phone
     * sit on screen until this fires, so the timeout is short and configurable
     * (dock.CheckInConfig.IdleResetSeconds) rather than a constant someone has
     * to redeploy to change.
     */
    const idleSeconds = config?.idleResetSeconds ?? IDLE_FALLBACK_SECONDS;

    /**
     * ⏱️ THE DONE SCREEN CLEARS ITSELF.
     *
     * The general idle reset is measured in a minute or more, which is right for
     * a driver halfway through typing but far too slow here: the next driver
     * walks up to somebody else's arrival number still on the glass. A finished
     * check-in has nothing left to do, so it goes back to the keypad quickly.
     *
     * ⚠️ Not a constant — DONE_RESET_SECONDS is the shipped default and the
     * facility's own idleResetSeconds wins when it is SHORTER, so a site that
     * has already tuned the kiosk down is not overridden by a longer number.
     */
    /**
     * ⛔⛤ ~~Math.min(DONE_RESET_SECONDS, idleSeconds)~~ — struck 2026-09-23
     * (migration 191). It meant a facility could only ever make this screen
     * SHORTER than five seconds and nothing could lengthen it.
     *
     * > Pete: *"the timeout seems much faster than that today??? like 10 seconds
     * > on the last screen. Is that set somewhere else?"* — it was: a constant in
     * this file, while the setting he could see said 90.
     *   ** A CONSTANT WITH A COMMENT CALLING IT A DEFAULT IS STILL A CONSTANT.
     *      THE TEST IS WHETHER ANYONE BUT A DEPLOY CAN CHANGE IT. **
     *
     * ⭐ The server now resolves every fallback (`toKioskView`), so these three
     * arrive as numbers and this page does no `??` of its own. The `?? idleSeconds`
     * below is only for an OLD SERVER that does not send them yet.
     */
    const doneSeconds    = config?.doneResetSeconds ?? DONE_RESET_SECONDS;
    const vehicleSeconds = config?.idleVehicleSeconds ?? idleSeconds;
    const safetySeconds  = config?.idleSafetySeconds ?? idleSeconds;
    /**
     * ⭐⭐ THE TIMER FOR THE SCREEN THE DRIVER IS ON.
     * ⚠️ The vehicle step is short on purpose — he walks out to read the number
     * off the box and must not come back to his own half-filled screen. The
     * safety step is LONG on purpose, and the comment below the idle effect has
     * said why since it was written: reading slowly and walking away look
     * identical from here.
     */
    const stepIdleSeconds =
        step === 'vehicle' ? vehicleSeconds
        : step === 'safety' ? safetySeconds
        : idleSeconds;
    const [doneLeft, setDoneLeft] = useState(doneSeconds);
    useEffect(() => {
        if (step !== 'done') { setDoneLeft(doneSeconds); return; }
        setDoneLeft(doneSeconds);
        const t = window.setInterval(() => {
            setDoneLeft(v => {
                if (v <= 1) { window.clearInterval(t); reset(); return 0; }
                return v - 1;
            });
        }, 1000);
        return () => window.clearInterval(t);
    }, [step, doneSeconds, reset]);
    const idleTimer = useRef<number | undefined>(undefined);
    useEffect(() => {
        const bump = () => {
            window.clearTimeout(idleTimer.current);
            idleTimer.current = window.setTimeout(() => {
                // Nothing to clear on a fresh screen; do not fight a driver who
                // is simply reading the safety notice slowly... but do clear it,
                // because "reading slowly" and "walked away" look identical and
                // only one of them is safe to assume.
                reset();
            }, Math.max(10, stepIdleSeconds) * 1000);
        };
        bump();
        const evts: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart'];
        evts.forEach(e => window.addEventListener(e, bump));
        return () => {
            window.clearTimeout(idleTimer.current);
            evts.forEach(e => window.removeEventListener(e, bump));
        };
        /* ⛔ stepIdleSeconds, NOT idleSeconds. Listing the general value here
           would leave the timer on whatever the first render saw and the
           per-screen settings would do nothing — silently, because a timeout
           that is too long looks like a timeout that has not fired yet. */
    }, [stepIdleSeconds, reset]);

    /**
     * ⭐ 'KIOSK' — the driver check-in tablet is a kiosk surface, and migration
     * 054 gave it its own AppCode.
     * ⛔ THE READ IS ANONYMOUS AND MUST STAY THAT WAY (arch §1D). `/api/brand`
     * takes no token and returns no identifiers; a driver has no account and
     * this page could not present one. ⚠️ useBranding starts at the shipped
     * defaults and swaps in the company's if and when they arrive, so a slow or
     * failed fetch shows the wordmark it shows today rather than nothing.
     *
     * ⛔⛔ IT LIVES UP HERE WITH THE OTHER HOOKS, AND THAT IS NOT A STYLE CHOICE.
     * Moved 2026-09-08 because it was called BELOW the `if (locked)` and
     * `if (!config)` early returns. `config` starts null and arrives from an
     * effect, so the first render returned early with N hooks and the next one
     * reached this line and called two more -- React error #310, "rendered more
     * hooks than during the previous render". ⚠️ React then unmounts the tree,
     * so the symptom is a COMPLETELY BLANK PAGE with the real cause only in the
     * console. Nothing renders, so nothing hints at branding.
     * ⛔ ANY hook added to this component must go above line ~470. Below the
     * early returns is not "later in the file", it is a different number of
     * hooks on different renders.
     */
    const branding = useBranding('KIOSK');

    const digits = phone.replace(/\D+/g, '');
    const prettyPhone = formatPhoneDigits(digits);

    /**
     * ---- ⛔ `lookup()` AND `searchPaperwork()` LIVED HERE ----------------------
     *
     * They asked the server which loads this driver appeared to have, and drove the
     * pick-a-load screen. Both are gone with it (2026-08-25).
     *
     * ⭐ THE MATCHER ITSELF IS UNTOUCHED AND STILL THE ONLY ONE. The scheduler's side
     * calls the same `lookupForKiosk` through `GET mgmt/checkins/{id}/candidates`, run
     * AFTER the licence scan — when the identifiers are better than a typed phone
     * number, and when a person is there to judge the answer.
     *
     * ⚠️ `postKioskLookup` REMAINS ON THE SERVER and is no longer called from here.
     * That is deliberate, not an oversight: it is the endpoint the matcher is exercised
     * through, and deleting it would take the tested path with it.
     */


    /**
     * ⛔ `choose()` LIVED HERE — the driver confirming or picking a load. Gone with the
     * screen that called it (2026-08-25): the scheduler makes that association now,
     * after the licence scan.
     */


    /**
     * ---- ⭐⭐ THE OPTIONAL VEHICLE OVERRIDE (2026-09-23) --------------------
     *
     * ⛔ `classifyVehicle` CALLS THIS ON THE SAME TICK IT SETS THE THREE FIELDS,
     * and React has not applied those setters yet. Without the argument this
     * function would read the pre-tap values and post an empty vehicle — quietly,
     * with a 200 back, and a gate row that says the driver had no number.
     *   ** A CHAIN THAT DROPS A VALUE DOES NOT THROW. IT RETURNS THE DEFAULT, AND
     *      THE DEFAULT LOOKS LIKE A CHOICE. **
     *
     * ⚠️ ABSENT MEANS "READ THE STATE", which is what the phone step and the
     * sign-off step both want: by the time they submit, the setters have applied.
     */
    async function submit() {
        setSubmitting(true);
        setError(null);
        try {
            const r = await postKioskCheckIn({
                driverName: name.trim() || undefined,
                driverPhone: digits || undefined,
                carrierCode: carrier.trim() || undefined,
                // Only sent when we could not derive it. The server takes the
                // appointment's direction over anything the driver typed.
                // ⚠️ ALWAYS SENT NOW. Nothing is matched at the gate any more, so there
                // is no appointment to take a direction from — this is the only answer
                // we have until the desk attaches a load.
                direction,
                // '' stays undefined; '0' becomes 0. An empty trailer is not the
                // same as an unanswered question.
                /**
                 * ⚠️ `0` IS A REAL VALUE ON THE WIRE AND MEANS "I DO NOT KNOW".
                 * `readPalletCount` on the server turns it into the pending flag and
                 * stores no number — the tablet does not make that decision, it only
                 * reports what was typed.
                 * ⛔ AN EMPTY BOX STAYS `undefined`, which is the driver skipping. The
                 * server treats both the same way; sending `0` for a skip would be
                 * the tablet answering a question he declined.
                 *   ** "SKIPPED" AND "SAID ZERO" ARRIVE AS DIFFERENT THINGS EVEN
                 *      WHERE THEY ARE TREATED THE SAME. **
                 */
                palletCount: pallets.trim() === '' ? undefined : Number(pallets.trim()),
                // ⚠️ ONE TYPED VALUE, TWO POSSIBLE HOMES. An ISO 6346 container id
                // is unmistakable (four letters then seven digits), so it goes to
                // the container column -- the one field both WMSes leave empty and
                // the reason capturing it here was a phase-1 goal. Everything else
                // is stored as a reference, because we genuinely cannot tell a PO
                // from a BOL from a pickup number by looking at it.
                //
                // The honest fix is dock.AppointmentReference (type + value), which
                // is task #74. Until then this is a two-way guess, not a taxonomy.
                /* ⭐ THE FIELD HE WAS ASKED FOR WINS. The ISO 6346 guess below it
                   only speaks when the dedicated field is empty — which is every
                   time the container flag is off, and the free paperwork field is
                   not rendered anyway. ⛔ Stated rather than left to evaluation
                   order: `a || b` is a precedence decision, and an undeclared one
                   is the kind that gets "tidied" into the wrong order later. */
                containerNumber: containerNo.trim().toUpperCase() || undefined,
                /**
                 * ---- ⛔⛤ THE ISO 6346 GUESS IS GONE (2026-09-23) ------------
                 *
                 * This used to read `paperwork` — one free-text box — and guess:
                 * four letters then seven digits went to `containerNumber`,
                 * everything else to `referenceNumber`. Its own comment called it
                 * *"a two-way guess, not a taxonomy"* and pointed at #74.
                 *
                 * ⭐ THE DRIVER NOW SAYS WHICH IT IS, on two screens with three
                 * buttons each. A guess that survives next to an answer is a guess
                 * that will overrule it the first time somebody reorders these
                 * lines.
                 *   ** AN INSTRUCTION WRITTEN BEFORE THE THING THAT MAKES IT
                 *      UNNECESSARY IS NOT STILL THE INSTRUCTION. **
                 *
                 * ⚠️ AND IT WAS ALREADY DEAD. `paperwork` had no input bound to it
                 * after the long form was removed — `reset()` was its only writer —
                 * so every branch above has been evaluating `''` for weeks. The box
                 * exists again now, and it feeds the three fields below instead.
                 */
                poNumber:        poNo.trim().toUpperCase() || undefined,
                bolNumber:       bolNo.trim().toUpperCase() || undefined,
                referenceNumber: refNo.trim().toUpperCase() || undefined,
                /**
                 * ⛔ SENT AS TYPED, UPPERCASED. ⚠️ Trailer numbers and plates are
                 * written in mixed case on the box and quoted in caps everywhere else;
                 * normalising here is what makes "abc1234" and "ABC1234" the same
                 * trailer to the search that has to find it later (#145).
                 */
                trailerNumber: trailerNo.trim().toUpperCase() || undefined,
                trailerPlate: trailerPlate.trim().toUpperCase() || undefined,
                /**
                 * ⛔ NO LANGUAGE IS SENT ANY MORE, AND SENDING 'English' WOULD BE WORSE
                 * THAN SENDING NOTHING. The picker was removed 2026-08-25; the state
                 * behind it defaulted to English, so keeping the line would stamp every
                 * driver on the platform as an English speaker on the strength of a
                 * question nobody asked. `NULL` is the truth: we did not ask.
                 */
                foodSafetyAck: agreed || undefined,
                foodSafetyWordingVer: config?.foodSafetyWordingVer,
                /**
                 * ⛔ NEVER SET FROM THE KIOSK ANY MORE. The driver does not pick a load,
                 * and the server no longer infers one for a kiosk arrival either — see
                 * recordCheckIn. The desk attaches it, after the licence is scanned.
                 * ⚠️ The field stays in the request shape: a STAFF-entered check-in still
                 * uses it, and that is a person choosing, which was always allowed.
                 */
                appointmentId: undefined,
            }, cfgKey);
            setResult(r);
            setStep('done');
        } catch (err) {
            const e = err as Error & { status?: number; reload?: boolean };
            if (e.status === 409 && e.reload) {
                // The wording changed while he was reading it. Refetch and send
                // him back to read the new text — an acknowledgement recorded
                // against words he never saw is worth nothing.
                await loadConfig();
                setAgreed(false);
                setStep('safety');
                setError(both('reread'));
            } else if (e.status === 429) {
                setError(both('busy'));
            } else if (e.status === 403) {
                setLocked(true);
            } else {
                setError(e.message || 'Please see the office / Vaya a la oficina');
            }
        } finally {
            setSubmitting(false);
        }
    }

    if (locked) {
        return (
            <div className="ci-root">
                <style>{CSS}</style>
                {/**
                  * ---- ⭐⭐ MATCHED TO THE LABOUR CLOCK (Pete, 2026-09-24) ----------
                  *
                  * > *"Can you make the driver checkin screen match the one you just
                  * > built?"* - after the clock's refusal card was rebuilt from THIS
                  * > screen's own geometry.
                  *
                  * ⚠️ THE TRAFFIC WENT BOTH WAYS, ON PURPOSE. The clock took this
                  * card's box, type scale and wording; this screen takes the clock's
                  * red X and red edge. Neither is now the "original" - they are one
                  * treatment with two mounts, which is the only version of
                  * consistent that survives the next change to either.
                  *   ** COPYING IN ONE DIRECTION LEAVES A MASTER AND A COPY. AGREEING
                  *      ON A TREATMENT LEAVES TWO PEERS. **
                  *
                  * ⛔ THE X IS A GLYPH, NOT AN ICON IMPORT. This page ships no icon
                  * library - it is a gate tablet that must paint on a cold cache over
                  * a bad connection, and one character costs nothing.
                  */}
                <div className="ci-card ci-locked">
                    <div className="ci-locked-x" aria-hidden="true">&#10007;</div>
                    <div className="ci-h1"><E k="locked" /></div>
                    <div className="ci-sub"><E k="lockedWhy" /></div>
                </div>
            </div>
        );
    }

    if (!config) {
        return (
            <div className="ci-root">
                <style>{CSS}</style>
                <div className="ci-card"><div className="ci-sub">…</div></div>
            </div>
        );
    }

    const canLeavePhone = digits.length >= 10 || name.trim().length > 0;
    /**
     * ---- ⭐⭐ THE CONFIG FLAGS THE KIOSK NEVER READ (#73, 2026-09-22) --------
     *
     * ⛔⛔ UNTIL TODAY THIS PAGE READ THREE CONFIG KEYS — `foodSafetyText`,
     * `requireFoodSafetyAck`, `smsNoticeText` — and nothing else. Meanwhile
     * `driverCheckIn.ts` gates the WRITE on `collectTrailerNumber` /
     * `collectContainerNumber`, and `CheckInConfigAdmin` renders a toggle for
     * each. So the admin could switch "Trailer number" off and on all day and the
     * only effect available was on the server: an answer the driver had given
     * being thrown away on arrival.
     *   ** A FLAG READ ON ONE SIDE OF THE WIRE AND NOT THE OTHER IS NOT A SETTING.
     *      IT IS TWO PROGRAMS DISAGREEING, WITH A SWITCH ON TOP. **
     *
     * ⚠️ THIS FILE ALREADY KNEW THE RULE. The note on `collectLanguage` a few
     * hundred lines below says it outright — *"THE ADMIN TOGGLE MUST NOT PRETEND
     * TO WORK"* — and for language the admin page says so on screen. These two
     * toggles had the same fault and no such notice.
     */
    const asksVehicle = config.collectTrailerNumber || config.collectContainerNumber;
    /**
     * ⛔ ONE FLAG FOR THE WHOLE PAPERWORK SCREEN. `collectPoNumber` turns it on
     * and all three buttons ride it — exactly as the plate rides
     * `collectTrailerNumber` (migration 161's precedent). Three flags would be
     * three ways to configure one screen into nonsense.
     */
    const asksPaperwork = config.collectPoNumber;
    /* ⚠️ `?? false` — an OLDER SERVER does not send this field, and the honest
       reading of a missing flag is "this site has not been asked to collect it",
       not "collect it". A new screen must not appear because a deploy was half
       done. */
    const asksPallets = config.collectPalletCount ?? false;

    /**
     * ---- ⭐⭐ THE ORDER LIVES HERE AND NOWHERE ELSE -----------------------
     *
     * ⛔ EVERY "Next" ASKS THIS FUNCTION, rather than naming the screen after it.
     * The two-step version already spelled the order out in the phone handler AND
     * the vehicle handler, and adding a third screen would have made it four
     * copies — each of which is a place to forget one.
     *
     * ⚠️ `'submit'` IS A DESTINATION. A site that collects nothing and asks for
     * no sign-off goes from the keypad straight in, which is the behaviour the
     * phone step has always had; saying it here keeps that a consequence of the
     * chain rather than a special case somebody has to remember.
     */
    const CHAIN: Step[] = ['vehicle', 'paperwork', 'pallets', 'review'];
    const stepIsAsked = (st: Step): boolean =>
        st === 'vehicle'   ? asksVehicle
      : st === 'paperwork' ? asksPaperwork
      : st === 'pallets'   ? asksPallets
      /* ⚠️ REVIEW EXISTS ONLY IF THERE IS SOMETHING TO REVIEW. A confirmation
         screen listing three blanks is a tap that teaches a driver the screen is
         noise, and he will tap through the next one too. */
      : st === 'review'    ? (asksVehicle || asksPaperwork || asksPallets)
      : true;
    const nextAfter = (from: Step | null): Step | 'submit' => {
        const i = from === null ? -1 : CHAIN.indexOf(from);
        for (let k = i + 1; k < CHAIN.length; k++) {
            if (stepIsAsked(CHAIN[k])) return CHAIN[k];
        }
        return config.requireFoodSafetyAck ? 'safety' : 'submit';
    };
    const goNext = (from: Step | null) => {
        const d = nextAfter(from);
        if (d === 'submit') void submit();
        else setStep(d);
    };
    /* ⚠️ WHERE BACK GOES, which is NOT simply the previous array slot — a site
       that skips paperwork must send Back past it, not into it. */
    const prevBefore = (from: Step): Step => {
        const i = CHAIN.indexOf(from);
        for (let k = i - 1; k >= 0; k--) {
            if (stepIsAsked(CHAIN[k])) return CHAIN[k];
        }
        return 'phone';
    };
    /**
     * ---- ⭐⭐ WHETHER NEXT IS ALLOWED (#73, 2026-09-23) --------------------
     *
     * ⛔⛔ THIS ENABLES A BUTTON. IT DOES NOT ENFORCE ANYTHING. The route runs
     * the real rule (`lib/vehicleNumber.ts`, GATE 3b) and refuses in both
     * languages. A tablet is a thing a person can drive with `fetch`.
     *   ** A RULE THE CLIENT KEEPS IS NOT A RULE. IT IS A COURTESY. **
     *
     * ⚠️ THE LENGTH COMES FROM THE SERVER. `web/` is a separate tsconfig project
     * (`"include": ["src"]`) so it cannot import the rule module, and a copy of
     * the number here would be a second definition that drifts.
     * ⛔ `?? 0` MEANS "NO CLIENT-SIDE MINIMUM", which is what an older server that
     * does not send the field should get — the button enables and the server
     * answers. Inventing 3 here would be the drift this avoids.
     *
     * ⚠️ EVERY value is measured, not just the first. A good trailer number plus
     * a stray `1` in the plate box is a mistake worth catching at the gate rather
     * than storing a one-character plate. Matches the server exactly.
     *
     * ⛔ THE BYPASS CODE IS NOT KNOWN HERE AND DOES NOT NEED TO BE. It is exactly
     * the minimum length, so it satisfies this check as an ordinary value and the
     * server decides what it means.
     *   ** THE TABLET KNOWS A LENGTH. THE SERVER KNOWS A MEANING. **
     */
    /* ---- ⛔⛤ THE SMALLEST FLOOR, NOT THE FIELD'S OWN (2026-09-24) ----------
       The server now sends three floors (trailer 1, container 11, plate 4) and
       this gate deliberately uses the SMALLEST of them.

       ⛔⛔ ENFORCING THE PER-FIELD FLOOR HERE WOULD KILL THE BYPASS AT THE
       KEYBOARD. The code a driver types to say "I cannot read any of them" is
       three characters; a container box enforcing 11 would grey out Next and
       never tell him why, because a disabled button has nothing to say.
         ** THE MOST DANGEROUS CLIENT-SIDE VALIDATION IS THE KIND THAT REFUSES
            BY DOING NOTHING. **

       ⭐ SO THE TABLET ASKS ONLY "IS THIS PLAUSIBLY AN ANSWER", AND THE SERVER
       ANSWERS "IS IT THE RIGHT KIND OF ANSWER" — with a sentence naming the box,
       which is something this gate could never produce.
         ** THE TABLET KNOWS A LENGTH. THE SERVER KNOWS A MEANING. ** */
    const vehicleMins = config?.vehicleNumberMinLengths;
    const vehicleMin = vehicleMins
        ? Math.min(vehicleMins.trailerNumber, vehicleMins.containerNumber, vehicleMins.trailerPlate)
        : 0;
    /* ⚠️ STILL READS ALL FOUR. The box is what the driver fills; the other three
       are what `classifyVehicle` wrote into, and they are non-empty when he comes
       BACK from the sign-off screen. Reading only the box would call a returning
       driver's completed screen empty. */
    const vehicleTyped = [vehicleValue, trailerNo, containerNo, trailerPlate]
        .map(v => v.trim())
        .filter(v => v !== '');
    const vehicleReady =
        vehicleTyped.length > 0
        && (vehicleMin === 0 || vehicleTyped.every(v => v.length >= vehicleMin));

    /* ---- ⛔⛤ NOT THE VEHICLE RULE. ITS OWN (2026-09-24) -------------------
       ⛔ THE COMMENT THAT WAS HERE SAID: *"the server applies one length to both
       (see src/lib/kioskCodes.ts, which now owns it), so the tablet must not
       invent a second."* That was TRUE: `kioskCodes.ts` owns one
       number and both modules read it. ⚠️ What changed is that the VEHICLE half
       stopped reading it on 2026-09-24 — trailer 1, container 11, plate 4 — while
       the paperwork half still does. So this gate must now read the paperwork
       floor by name instead of borrowing the vehicle one.
       ⚠️ It was harmless only while both rules wanted 3. The moment the vehicle
       floors became per-field, a PO box would have inherited 1 or 11 depending on
       which value happened to be read. See src/lib/paperworkNumber.ts. */
    const paperworkMin = config?.paperworkMinLength ?? 0;
    const paperTyped = [paperwork, poNo, bolNo, refNo]
        .map(v => v.trim())
        .filter(v => v !== '');
    const paperReady =
        paperTyped.length > 0
        && (paperworkMin === 0 || paperTyped.every(v => v.length >= paperworkMin));

    /**
     * ---- ⭐⭐ "THIS IS MY …" — CLASSIFY AND GO (Pete, 2026-09-23) ---------
     *
     * ⛔ IT CLEARS THE OTHER TWO EVERY TIME. A driver who taps CONTAINER, comes
     * back from the sign-off screen and taps TRAILER would otherwise send both —
     * and the server would store two numbers for a truck that has one.
     *   ** A CLASSIFIER THAT ONLY EVER SETS IS A CLASSIFIER THAT ACCUMULATES. **
     *
     * ⚠️ THE ROUTING IS THE SAME RULE AS THE PHONE STEP, not a second copy of
     * the order: sign-offs next where the site asks for them, otherwise straight in.
     * ⚠️ At Brighton and Commerce City `RequireFoodSafetyAck = 0`, so on both
     * live sites this button IS the submit — hence the `submitting` guard.
     */
    /**
     * ---- ⭐⭐⭐ THE THREE COLUMNS ARE WRITTEN IN ONE PLACE (2026-09-23) -------
     *
     * ⛔ EXTRACTED BECAUSE THE REVIEW SCREEN NOW WRITES THEM TOO. Pete asked for an
     * Edit on the summary, so the "exactly one of these three is non-empty" rule
     * has a SECOND caller - and two copies of that rule is how a driver ends up
     * with a container number AND a trailer number, which is a truck we cannot
     * describe.
     *   ** AN INVARIANT WITH TWO WRITERS IS AN INVARIANT WITH TWO CHANCES TO BE
     *      WRONG. **
     * ⚠️ IT DOES NOT TRIM OR UPPERCASE. Both callers already hold the cleaned
     * value, and doing it twice would hide which one was responsible.
     */
    const writeVehicle = (kind: 'container' | 'trailer' | 'plate', v: string) => {
        setContainerNo(kind === 'container' ? v : '');
        setTrailerNo(kind === 'trailer' ? v : '');
        setTrailerPlate(kind === 'plate' ? v : '');
    };
    const writePaper = (kind: 'po' | 'bol' | 'ref', v: string) => {
        setPoNo(kind === 'po' ? v : '');
        setBolNo(kind === 'bol' ? v : '');
        setRefNo(kind === 'ref' ? v : '');
    };

    /**
     * ⭐⭐ WHICH TYPE HE PRESSED, DERIVED - NOT STORED.
     *
     * ⛔ NO NEW STATE. The answer is already in the data: exactly one of the three
     * columns is non-empty, and that IS the classification. A `vehicleKind` state
     * would be a second copy that can disagree with the columns it describes, and
     * the disagreement would be invisible until a receipt named the wrong field.
     *   ** DERIVE WHAT THE DATA ALREADY SAYS. A CACHED ANSWER TO A QUESTION THE
     *      DATA ANSWERS IS A BUG WITH A DELAY ON IT. **
     * ⚠️ NULL BEFORE HE HAS CLASSIFIED ANYTHING, which is every render before the
     * review screen can be reached.
     */
    const vehicleKind: 'container' | 'trailer' | 'plate' | null =
        containerNo ? 'container' : trailerNo ? 'trailer' : trailerPlate ? 'plate' : null;
    const paperKind: 'po' | 'bol' | 'ref' | null =
        poNo ? 'po' : bolNo ? 'bol' : refNo ? 'ref' : null;
    const VEHICLE_NOUN = { container: 'nounContainer', trailer: 'nounTrailer', plate: 'nounPlate' } as const;
    const PAPER_NOUN = { po: 'nounPo', bol: 'nounBol', ref: 'nounRef' } as const;

    /* ⭐ `editing` LIVES WITH THE OTHER STATE, ABOVE THE EARLY RETURNS.
       It was declared HERE and that took the kiosk down - see its new home. */

    const classifyVehicle = (kind: 'container' | 'trailer' | 'plate') => {
        if (submitting || !vehicleReady) return;
        const v = vehicleValue.trim().toUpperCase();
        /* ⛔ THE NARROW COLUMN GETS ITS ANSWER ON THE TABLET. See `plateLong`. */
        if (kind === 'plate' && v.length > 20) {
            setError(both('plateLong'));
            return;
        }
        setError(null);
        writeVehicle(kind, v);
        /* ⛔ PASSED EXPLICITLY, NOT READ BACK FROM STATE. The three `setX` calls
           above have NOT applied on this tick, so a `submit()` that read the state
           would send the PREVIOUS classification — or, first time through, nothing
           at all, and a blank vehicle is indistinguishable from a driver who had
           no number.
             ** A SETTER IS A REQUEST, NOT AN ASSIGNMENT. WHAT YOU NEED THIS TICK
                YOU MUST CARRY YOURSELF. ** */
        /**
         * ⭐⭐ AND IT NO LONGER SUBMITS, which is why `submit()` lost its override
         * argument the same day it gained one.
         *
         * ⛔⛤ For three hours this called `submit({ container, trailer, plate })`,
         * passing the values explicitly because the three `setX` calls above have
         * NOT applied on this tick. That was correct while a classify button was
         * the last thing a driver touched. The review screen now sits between this
         * tap and the submission, so by the time Confirm is pressed React has long
         * since applied them.
         *   ** A WORKAROUND OUTLIVES THE PROBLEM UNLESS SOMEBODY DELETES IT, AND
         *      AN ARGUMENT NOBODY PASSES IS READ AS ONE SOMEBODY FORGOT TO. **
         */
        goNext('vehicle');
    };

    /**
     * ---- ⭐⭐ THE SAME CLASSIFIER, FOR THE PAPERWORK (Pete, 2026-09-23) ----
     *
     * > *"the exact same screen but asking for PO number, Reference Number or BOL.
     * > Same layout, same buttons"*
     *
     * ⚠️ DELIBERATELY A NEAR-COPY OF `classifyVehicle` RATHER THAN A SHARED
     * HELPER. The two differ in which three setters they call and where they go
     * next, and a generic version would take both of those as parameters — at
     * which point it is the same number of lines, harder to read, and the place a
     * future third screen's special case gets bolted on.
     *   ** TWO THINGS THAT ARE THE SAME SHAPE TODAY ARE NOT THEREFORE ONE THING. **
     * ⛔ IT CLEARS THE OTHER TWO EVERY TIME, same as the vehicle one: a driver who
     * taps PO, comes back from review and taps BOL would otherwise send both.
     */
    const classifyPaperwork = (kind: 'po' | 'bol' | 'ref') => {
        if (submitting || !paperReady) return;
        const v = paperwork.trim().toUpperCase();
        setError(null);
        writePaper(kind, v);
        goNext('paperwork');
    };

    return (
        <div className="ci-root">
            <style>{CSS}</style>
            {/* ⭐ DATA, NOT A LITERAL (2026-09-07). This read `SYMBIA`, hardcoded,
                which is why this tablet stayed Symbia-branded no matter what a
                customer set. ⚠️ The CSS uppercases it, so a company name in any
                case still renders as a wordmark. */}
            <div className="ci-brand">{branding.companyName}</div>

            <div className="ci-card">
                {error && <div className="ci-error">{error}</div>}

                {step === 'phone' && (
                    <>
                        <div className="ci-h1"><E k="phoneQ" /></div>
                        <div className="ci-sub"><E k="phoneHelp" /></div>
                        {/**
                          * ⭐⭐ THE TEXTING NOTICE, WHERE HE TYPES THE NUMBER (2026-08-27).
                          *
                          * Pete: *"We can put a blurb saying by entering their cell number,
                          * they are consenting even though we are not saving it off."*
                          *
                          * ⛔⛔ THIS IS A NOTICE AND NOT A RECORDED CONSENT, and the code
                          * must not pretend otherwise. Nothing writes `dbo.SmsConsent` from
                          * a check-in — that is #323. What this does is tell the driver, at
                          * the moment he hands the number over, what it will be used for.
                          *
                          * ⭐ IT IS SHOWN AT THE PHONE STEP AND NOWHERE ELSE, because that
                          * is the moment it is about. A notice on the confirmation screen
                          * would be a notice after the fact.
                          *
                          * ⭐ AND "REPLY STOP" IS A PROMISE THE SYSTEM ACTUALLY KEEPS — the
                          * inbound webhook is live, STOP writes `opted_out`, and the engine
                          * refuses to send from then on. ⚠️ Which is why the sentence says
                          * STOP and does not say anything we cannot deliver.
                          *
                          * ⚠️ THE SERVER OWNS THE WORDS, and it carries a version, so #323
                          * can record which notice a given driver was shown.
                          */}
                        {config?.smsNoticeText && (
                            <div className="ci-note">{config.smsNoticeText}</div>
                        )}
                        <div className="ci-phone">{prettyPhone || ' '}</div>
                        <div className="ci-pad">
                            {/* ⚠️ CLEAR SITS BESIDE 0, WHERE THE BLANK KEY USED TO BE.
                                A driver who mistypes early would otherwise press
                                backspace eight times. It clears the number only --
                                not the name, and not the step. */}
                            {['1','2','3','4','5','6','7','8','9','C','0','⌫'].map((d, i) => (
                                <button
                                    key={i}
                                    className={`ci-key ${d === 'C' ? 'ci-key-clear' : ''}`}
                                    /* ⚠️ THROUGH `both`, LIKE EVERY OTHER NON-JSX STRING. A
                                       screen reader announcing "Clear / Borrar" on an
                                       English-only tablet is the same bug as the visible
                                       one, and it is the half nobody would have seen. */
                                    aria-label={d === 'C' ? both('clear')
                                              : d === '⌫' ? both('backspace')
                                              : d}
                                    onClick={() => {
                                        if (d === '⌫') setPhone(digits.slice(0, -1));
                                        else if (d === 'C') setPhone('');
                                        else setPhone((digits + d).slice(0, 15));
                                    }}
                                >{d}</button>
                            ))}
                        </div>
                        {/* ⚠️ ASKED ONLY WHEN WE CANNOT DERIVE IT. A matched appointment
                            already carries Direction as a fact; asking a driver to restate
                            it just invites him to contradict the booking, and the panel
                            prefers the appointment's answer anyway. So this appears only
                            before a match is known. */}
                        {/* ⭐ ALWAYS ASKED NOW. It used to be hidden once a load was
                            matched at the gate; nothing is matched at the gate any more,
                            and which way the truck is pointing is the one thing the desk
                            cannot read off a licence. */}
                        {(
                            <div className="ci-field">
                                <span className="ci-label"><E k="dirQ" /></span>
                                {/**
                                  * ---- ⭐ A CHOSEN ANSWER MUST NOT LOOK LIKE A BUTTON TO PRESS
                                  *
                                  * Pete, 2026-08-25: *"Can you also put a little checkmark or some
                                  * type of indicator on the Delivering or Pickup when selected? Kind
                                  * of hard to tell if they are suppose to click it again or just hit
                                  * the Next button."*
                                  *
                                  * ⛔ THE SELECTED STATE WAS THE SAME BLUE AS `Next`, WHICH IS THE
                                  * WHOLE PROBLEM. A filled primary-coloured button is the app's own
                                  * word for "press this" — so the selected answer was saying press me
                                  * in exactly the moment it meant already chosen. A driver reading it
                                  * as an instruction is reading it correctly.
                                  *
                                  * ⭐ A TICK SAYS "CHOSEN" IN A WAY COLOUR CANNOT. It also survives
                                  * the two things colour does not: a sunlit tablet at a gate, and the
                                  * ~8% of men who cannot separate these hues.
                                  *
                                  * ⚠️ `aria-pressed` IS THE POINT, NOT DECORATION. These are toggles,
                                  * not commands, and that is the only thing that tells a screen reader
                                  * which one is on. The tick itself is aria-hidden — it would
                                  * otherwise be announced as a stray character.
                                  *
                                  * ⚠️ THE SPACE IS RESERVED EVEN WHEN EMPTY, so choosing does not
                                  * shove the label sideways.
                                  */}
                                <div className="ci-row ci-dir">
                                    <button
                                        type="button"
                                        aria-pressed={direction === 'INBOUND'}
                                        className={`ci-choice ${direction === 'INBOUND' ? 'is-on' : ''}`}
                                        onClick={() => setDirection('INBOUND')}
                                    >
                                        <span className="ci-tick" aria-hidden="true">
                                            {direction === 'INBOUND' ? '\u2713' : ''}
                                        </span>
                                        <E k="delivering" />
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={direction === 'OUTBOUND'}
                                        className={`ci-choice ${direction === 'OUTBOUND' ? 'is-on' : ''}`}
                                        onClick={() => setDirection('OUTBOUND')}
                                    >
                                        <span className="ci-tick" aria-hidden="true">
                                            {direction === 'OUTBOUND' ? '\u2713' : ''}
                                        </span>
                                        <E k="pickingUp" />
                                    </button>
                                </div>
                            </div>
                        )}
                        {/**
                          * ---- ⛔ PREFERRED LANGUAGE IS NOT ASKED HERE ANY MORE -------
                          *
                          * Pete, 2026-08-25: *"asking them that everytime kinda is a waste
                          * of their time right? Lets drop the preferred language for now."*
                          *
                          * ⭐ AND HE IS RIGHT FOR A REASON WORTH WRITING DOWN: NOTHING WAS
                          * REMEMBERING THE ANSWER. `dock.DriverCheckIn.Language` is stamped
                          * per VISIT; `dock.DriverIdentity` — the row that persists across
                          * visits and is keyed on the licence — has no language column at
                          * all. So a driver who told us "Punjabi" in March was asked again
                          * in April, and again in May. It was a question with no memory.
                          *
                          * ⚠️ THE COLUMN AND THE CONFIG FLAG BOTH SURVIVE, deliberately.
                          * `collectLanguage` still exists and the historic values are still
                          * on the rows; removing them would destroy answers already given.
                          * ⛔ BUT THE ADMIN TOGGLE MUST NOT PRETEND TO WORK — a setting that
                          * changes nothing is how somebody spends an afternoon on a field
                          * with no effect (the same fault as the "Scan paperwork" button).
                          * CheckInConfigAdmin says so on screen.
                          *
                          * 🔨 It comes back on the identity, once, when there is somewhere
                          * to remember it — see the task.
                          */}
                        {/* A driver with no phone is still a truck in the yard, so the
                            step can be passed with a name instead. */}
                        {!canLeavePhone && <div className="ci-hint"><E k="needSome" /></div>}
                        {/**
                          * ---- ⭐ PHONE, THEN THE SIGN-OFFS, THEN THE DESK -------------
                          *
                          * Pete, 2026-08-25: *"Enter the cell phone number, if driver not in
                          * the system, then say go to desk/scheduler?"* and *"Food safety ack
                          * lives on the tablet… After the cell phone number goes in, then the
                          * sign-off docs, then direct them to the check in desk."*
                          *
                          * ⛔ THE LOOKUP IS GONE FROM THE DRIVER'S PATH. It used to run here
                          * and, on a hit, offer him a list of loads to pick from. Pete: *"I
                          * don't think we are giving the driver the ability to assign
                          * themselves to an appointment. They are not going to look at the
                          * paperwork and confirm the info consistently and accurately."*
                          *
                          * ⛔ AND THE SERVER STOPPED INFERRING A MATCH FOR KIOSK ARRIVALS TOO
                          * (see recordCheckIn). Removing only the screen would have changed
                          * nothing a scheduler could see — the card would still have arrived
                          * pre-attached on an inference nobody confirmed.
                          *
                          * ⚠️ SO EVERY KIOSK ARRIVAL NOW REACHES THE PANEL UNMATCHED, BY
                          * DESIGN. That is not a regression to fix later; it is the point.
                          */}
                        <button
                            className="ci-primary"
                            disabled={!canLeavePhone || looking}
                            onClick={() => {
                                // ⚠️ Straight to the check-in when this site asks for no
                                // sign-off — an empty screen between the keypad and the
                                // answer is a step a driver has to work out the point of.
                                /* ⭐ THE CONFIG DECIDES THE ROUTE, not a hardcoded
                                   order. Both flags off and this step never exists. */
                                /* ⭐ THE CHAIN DECIDES, not this handler. See
                                   `nextAfter` — `null` means "from the start". */
                                goNext(null);
                            }}
                        >{looking ? <E k="looking" /> : <E k="next" />}</button>
                    </>
                )}

                {/**
                  * ---- ⛔ THE 'found' AND 'details' SCREENS LIVED HERE ---------------
                  *
                  * 'found' offered the driver a list of loads to pick from; 'details' was
                  * the long form — name, carrier, pallets, any paperwork number, trailer,
                  * plate — shown to anyone the lookup missed.
                  *
                  * Pete, 2026-08-25, on hitting that form during testing: *"This is not
                  * what we wanted for streamline."* And on the picker: *"I don't think we
                  * are giving the driver the ability to assign themselves to an
                  * appointment. They are not going to look at the paperwork and confirm
                  * the info consistently and accurately."*
                  *
                  * ⭐ EVERYTHING THOSE SCREENS COLLECTED IS STILL COLLECTABLE — by the
                  * scheduler, at the desk, from the paperwork in the driver's hand rather
                  * than from his memory of it. That is a better source, and it is the
                  * person who has to live with the answer.
                  *
                  * ⚠️ The kiosk is now three beats: phone → sign-offs → go to the desk.
                  */}
                {/**
                  * ---- ⭐⭐ THE TRAILER / CONTAINER STEP (#73) -------------------
                  *
                  * ⛔ ONE QUESTION PER SCREEN, WHICH IS WHY IT IS A STEP AND NOT TWO
                  * MORE FIELDS UNDER THE KEYPAD. Pete chose the step; the phone
                  * screen already carries a keypad, a direction toggle and a hint.
                  *
                  * ⚠️ NOTHING HERE IS REQUIRED. A driver at a gate may be in a
                  * borrowed tractor with an unreadable plate, and a truck in the yard
                  * is a fact whether or not we learned its trailer number. Next is
                  * always enabled — the step informs, it does not interrogate.
                  *   ** A KIOSK THAT REFUSES TO LET SOMEBODY THROUGH HAS BECOME A
                  *      GATE IN THE WRONG SENSE. **
                  *
                  * ⚠️ PLAIN TEXT INPUTS, NOT THE NUMERIC PAD. Trailer and container
                  * ids are alphanumeric (`ABC1234`, `MSCU1234567`), so the twelve-key
                  * pad above cannot enter them.
                  * ⭐ ASKED AND ANSWERED, 2026-09-22 — Pete: *"Kiosk is not locked
                  * down."* So the device's own keyboard rises on focus and a plain
                  * `<input>` is the whole requirement. ⛔ A LOCKED-DOWN KIOSK WOULD
                  * NEED AN ON-SCREEN ALPHANUMERIC PAD and this step would be
                  * uncompletable without one — recorded because the answer is a
                  * property of the HARDWARE, not of this file, and the next site to
                  * roll out may answer differently.
                  *
                  * ⭐ THE STYLING ALREADY EXISTED. `.ci-field input` is in this file's
                  * own style block, left from the removed long form, so this step
                  * introduces no new CSS and matches the screens around it.
                  */}
                {step === 'vehicle' && (
                    <>
                        <div className="ci-h1"><E k="vehicleQ" /></div>
                        {/**
                          * ---- ⛔⛤ ~~THE REASON AND THE HELP LINE~~ (Pete, 2026-09-23)
                          *
                          * > *"lets remove all the text between Your Trailer or Container
                          * > and the text box - not necessary and clogs the screen."*
                          *
                          * ⚠️ THE OLD NOTE HERE ARGUED THE OPPOSITE and was not wrong on
                          * its own terms: *"the reason sits above the box, where it is
                          * read before the question rather than after it."* That is good
                          * copy-placement advice for a FORM someone studies. It is the
                          * wrong instinct for a tablet a man taps in eight seconds with a
                          * truck running behind him.
                          *   ** THE READER'S BUDGET IS PART OF THE DESIGN. TWO LINES
                          *      NOBODY READS COST MORE THAN THEY EXPLAIN. **
                          *
                          * ⛔ THE STRINGS SURVIVE in the T table (`vehicleWhy`,
                          * `vehicleHelp`) - unrendered, not deleted, same as the Spanish.
                          * ⚠️ `vehicleNeed` BELOW THE BOX STAYS. It is not explanation,
                          * it is a REFUSAL telling him why the buttons are dead.
                          */}

                        {/**
                          * ⛔⛤ ~~THREE LABELLED FIELDS~~ — trailer, plate and container,
                          * each behind its own config flag, struck 2026-09-23 at Pete's
                          * request in favour of ONE box and three "this is my …" buttons.
                          *
                          * ⭐ WHY IT IS BETTER AND NOT JUST SHORTER: the old screen asked
                          * a driver to decide WHICH KIND of number he had before he had
                          * finished reading it, and offered three boxes he could fill in
                          * any combination — including all three, which is a truck we
                          * cannot describe. One box takes one number, and the button he
                          * presses says what it is. ⚠️ maxLength is 50 (the trailer and
                          * container width); the plate's 20 is enforced in
                          * `classifyVehicle`, which is the moment we learn it IS a plate.
                          */}
                        <label className="ci-field">
                            <span className="ci-label"><E k="vehicleAsk" /></span>
                            <input
                                type="text"
                                inputMode="text"
                                autoCapitalize="characters"
                                autoCorrect="off"
                                spellCheck={false}
                                maxLength={50}
                                /* ⭐ THE CURSOR IS ALREADY IN IT (Pete, 2026-09-23):
                                   *"should be lit up and bordered with the cursor on it
                                   immediately."* ⚠️ Safe here and NOT on the phone screen,
                                   which has its own twelve-key pad - focusing a field that
                                   the on-screen pad does not drive would raise the
                                   tablet's keyboard over it. */
                                autoFocus
                                value={vehicleValue}
                                onChange={e => setVehicleValue(e.target.value)}
                            />
                        </label>

                        {!vehicleReady && vehicleTyped.length > 0 && (
                            <div className="ci-hint"><E k="vehicleNeed" /></div>
                        )}

                        <div className="ci-which"><E k="vehicleWhich" /></div>
                        {/**
                          * ⚠️ THE ORDER IS CONTAINER, TRAILER, PLATE — the same
                          * precedence the server uses when it reports back what it stored
                          * (`vehicleRecorded`). Two orders for one set of three is how a
                          * screen and a receipt come to disagree.
                          *
                          * ⚠️ THE FLAGS STILL GATE THEM. A site that does not collect
                          * containers does not offer that answer — the box is one box, but
                          * the question it can be an answer TO is still the site's choice.
                          *
                          * ⛔ NO CANCEL HERE. There is a global Cancel further down that
                          * renders on every step but 'phone' and 'done'; the one that used
                          * to sit beside Next in this row was a SECOND one, which is the
                          * bug Pete reported ("two cancel buttons are showing").
                          *   ** A CONTROL THAT IS ALREADY RENDERED GLOBALLY IS NOT
                          *      MISSING FROM YOUR SCREEN. LOOK BEFORE ADDING IT. **
                          */}
                        <div className="ci-classify">
                            {config.collectContainerNumber && (
                                <button
                                    className="ci-classify-b"
                                    disabled={submitting || !vehicleReady}
                                    onClick={() => classifyVehicle('container')}
                                >{submitting ? <E k="working" /> : <E k="isContainer" />}</button>
                            )}
                            {config.collectTrailerNumber && (
                                <button
                                    className="ci-classify-b"
                                    disabled={submitting || !vehicleReady}
                                    onClick={() => classifyVehicle('trailer')}
                                >{submitting ? <E k="working" /> : <E k="isTrailer" />}</button>
                            )}
                            {config.collectTrailerNumber && (
                                <button
                                    className="ci-classify-b"
                                    disabled={submitting || !vehicleReady}
                                    onClick={() => classifyVehicle('plate')}
                                >{submitting ? <E k="working" /> : <E k="isPlate" />}</button>
                            )}
                        </div>
                    </>
                )}

                {/**
                  * ---- ⭐⭐ THE PAPERWORK STEP (Pete, 2026-09-23) ----------------
                  *
                  * > *"Lets add the exact same screen but asking for PO number,
                  * > Reference Number or BOL. Same layout, same buttons"*
                  *
                  * ⚠️ IT IS THE SAME MARKUP AS THE VEHICLE STEP ON PURPOSE. A driver
                  * who has just answered that one recognises this one without
                  * reading it, and at a gate that is worth more than variety.
                  *
                  * ⛔ ALL THREE BUTTONS RIDE `collectPoNumber`. There is no BOL flag
                  * and no reference flag — see `asksPaperwork`.
                  */}
                {step === 'paperwork' && (
                    <>
                        <div className="ci-h1"><E k="paperQ2" /></div>
                        {/* ⛔⛤ ~~paperWhy2 / paperHelp2~~ - see the vehicle screen. The
                            two screens are the same markup on purpose, so they lose the
                            same two lines. */}

                        <label className="ci-field">
                            <span className="ci-label"><E k="paperAsk" /></span>
                            <input
                                type="text"
                                inputMode="text"
                                autoCapitalize="characters"
                                autoCorrect="off"
                                spellCheck={false}
                                maxLength={50}
                                autoFocus
                                value={paperwork}
                                onChange={e => setPaperwork(e.target.value)}
                            />
                        </label>

                        {!paperReady && paperTyped.length > 0 && (
                            <div className="ci-hint"><E k="paperNeed" /></div>
                        )}

                        <div className="ci-which"><E k="vehicleWhich" /></div>
                        <div className="ci-classify">
                            <button
                                className="ci-classify-b"
                                disabled={submitting || !paperReady}
                                onClick={() => classifyPaperwork('po')}
                            ><E k="isPo" /></button>
                            <button
                                className="ci-classify-b"
                                disabled={submitting || !paperReady}
                                onClick={() => classifyPaperwork('bol')}
                            ><E k="isBol" /></button>
                            <button
                                className="ci-classify-b"
                                disabled={submitting || !paperReady}
                                onClick={() => classifyPaperwork('ref')}
                            ><E k="isRef" /></button>
                        </div>
                        {/**
                          * ⭐ BACK (Pete, 2026-09-23): *"There are no back buttons on
                          * paperwork and number of pallets screen."*
                          *
                          * ⛔ `prevBefore`, NOT `setStep('vehicle')`. A site that does not
                          * collect vehicle numbers has no vehicle screen to go back TO,
                          * and a hardcoded target would strand the driver on a screen the
                          * chain says does not exist.
                          * ⚠️ ITS OWN ROW, BELOW the three buttons - a Back sitting beside
                          * the classify buttons would read as a fourth answer.
                          */}
                        <div className="ci-row">
                            <button
                                className="ci-secondary"
                                onClick={() => setStep(prevBefore('paperwork'))}
                            ><E k="back" /></button>
                        </div>
                    </>
                )}

                {/**
                  * ---- ⭐⭐ THE PALLET STEP ------------------------------------
                  *
                  * > Pete: *"Very basic, numeric, and just like the others stored
                  * > with the driver."*
                  *
                  * ⛔ THE ONLY SKIPPABLE SCREEN, and Skip is a real button rather
                  * than a greyed-out Next, because "I do not know yet" is a true
                  * answer from a man who has not opened the doors.
                  * ⚠️ `inputMode="numeric"` RAISES THE NUMBER PAD on the tablet's own
                  * keyboard. The twelve-key pad on the phone screen is this file's
                  * own markup and is bound to the phone state; reusing it here would
                  * be a second binding of one component.
                  */}
                {step === 'pallets' && (
                    <>
                        <div className="ci-h1"><E k="palletQ" /></div>
                        <div className="ci-sub"><E k="palletWhy" /></div>
                        <div className="ci-hint"><E k="palletSkip" /></div>

                        <label className="ci-field">
                            <span className="ci-label"><E k="palletAsk" /></span>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoCorrect="off"
                                spellCheck={false}
                                maxLength={4}
                                autoFocus
                                value={pallets}
                                /* ⛔ DIGITS ONLY, FILTERED AS HE TYPES. The server
                                   refuses `12abc` rather than salvaging `12`, and a
                                   box that accepts what the server refuses teaches
                                   the driver the refusal is arbitrary. */
                                onChange={e => setPallets(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                        </label>

                        <div className="ci-row">
                            {/* ⭐ BACK - see the paperwork screen. ⚠️ FIRST in the row, so
                                "leave this screen backwards" and "leave it forwards" sit at
                                opposite ends and Skip is not between a driver and the
                                button he wanted. */}
                            <button
                                className="ci-secondary"
                                onClick={() => setStep(prevBefore('pallets'))}
                            ><E k="back" /></button>
                            <button
                                className="ci-secondary"
                                /* ⚠️ SKIP CLEARS THE BOX FIRST. A driver who typed
                                   `2`, thought better of it and tapped Skip must not
                                   have `2` submitted behind him. */
                                onClick={() => { setPallets(''); goNext('pallets'); }}
                            ><E k="skip" /></button>
                            <button
                                className="ci-primary"
                                disabled={submitting}
                                onClick={() => goNext('pallets')}
                            >{submitting ? <E k="working" /> : <E k="next" />}</button>
                        </div>
                    </>
                )}

                {/**
                  * ---- ⭐⭐ THE REVIEW STEP (Pete: *"the confirmation screen goes
                  * after the 2 screens to confirm both"*) -----------------------
                  *
                  * ⚠️ IT SHOWS WHAT HE TYPED, BYPASS CODE AND ALL. The tablet is
                  * never told the code — `toKioskView` sends the minimum LENGTH and
                  * withholds the code because this payload is unauthenticated
                  * (arch §1D) — so it cannot recognise a line it should hide. Put to
                  * Pete on 2026-09-23 with the alternative (publish the code to the
                  * tablet) and he chose this.
                  *   ** THE REVIEW SCREEN SHOWS A CLAIM. THE DONE SCREEN SHOWS A
                  *      FACT. ** — and the done screen is where a bypass correctly
                  * shows nothing, because the server answers that one.
                  *
                  * ⚠️ A ROW PER QUESTION THE SITE ASKED, not per value stored. A site
                  * that does not collect pallets has no pallet row; a site that does
                  * and got no answer shows "Not given", which is a different fact and
                  * a driver can still fix it with Back.
                  */}
                {step === 'review' && (
                    <>
                        <div className="ci-h1"><E k="reviewQ" /></div>
                        <div className="ci-sub"><E k="reviewWhy" /></div>

                        {/**
                          * ---- ⭐⭐⭐ THE ROWS NAME THE TYPE, AND EDIT IN PLACE --------
                          *
                          * > Pete, 2026-09-23: *"They also need to carry over what the
                          * > select in the title - its either trailer number, container
                          * > number or license number, not all three. ... You won't be
                          * > able to change the type but you should be able to change the
                          * > text right there because you already selected the type of
                          * > document. If they have to change that, they hit the back
                          * > button."*
                          *
                          * ⭐⭐ THE TYPE IS SETTLED AND THE VALUE IS NOT, and the screen
                          * now says so with its shape: the type is printed as a LABEL,
                          * the value sits behind an Edit. A driver who mistyped a digit
                          * fixes it here; a driver who pressed the wrong button goes Back.
                          *   ** AN EDIT CONTROL IS A CLAIM ABOUT WHAT IS STILL IN
                          *      QUESTION. **
                          *
                          * ⛔ THE LABEL FALLS BACK to the generic three-way string when
                          * `vehicleKind` is null. ⚠️ That is unreachable from the chain -
                          * the review step cannot be reached without classifying - but a
                          * label is not the place to prove a reachability argument.
                          */}
                        <div className="ci-rev-list">
                            {asksVehicle && (
                                <div className="ci-rev">
                                    <div className="ci-rev-l">
                                        {vehicleKind ? <E k={VEHICLE_NOUN[vehicleKind]} /> : <E k="revVehicle" />}
                                    </div>
                                    {editing === 'vehicle' ? (
                                        <input
                                            className="ci-rev-edit"
                                            type="text"
                                            autoCapitalize="characters"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            maxLength={50}
                                            autoFocus
                                            value={vehicleValue}
                                            /* ⛔ THE BOX AND THE COLUMN MOVE TOGETHER. Writing
                                               only `vehicleValue` would leave the review screen
                                               showing the new number while the SUBMITTED one
                                               stayed the old - the worst of both, because the
                                               screen that exists to confirm would be confirming
                                               something other than what it sends.
                                                 ** A CONFIRMATION THAT READS FROM A DIFFERENT
                                                    PLACE THAN THE SUBMIT IS NOT A
                                                    CONFIRMATION. ** */
                                            onChange={e => {
                                                const raw = e.target.value;
                                                setVehicleValue(raw);
                                                if (vehicleKind) writeVehicle(vehicleKind, raw.trim().toUpperCase());
                                            }}
                                            onBlur={() => setEditing(null)}
                                            onKeyDown={e => { if (e.key === 'Enter') setEditing(null); }}
                                        />
                                    ) : (
                                        <div className="ci-rev-row">
                                            <div className={'ci-rev-v' + (vehicleValue.trim() ? '' : ' is-none')}>
                                                {vehicleValue.trim().toUpperCase() || <E k="notGiven" />}
                                            </div>
                                            <button className="ci-rev-edit-b" onClick={() => setEditing('vehicle')}>
                                                <E k="edit" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {asksPaperwork && (
                                <div className="ci-rev">
                                    <div className="ci-rev-l">
                                        {paperKind ? <E k={PAPER_NOUN[paperKind]} /> : <E k="revPaper" />}
                                    </div>
                                    {editing === 'paper' ? (
                                        <input
                                            className="ci-rev-edit"
                                            type="text"
                                            autoCapitalize="characters"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            maxLength={50}
                                            autoFocus
                                            value={paperwork}
                                            onChange={e => {
                                                const raw = e.target.value;
                                                setPaperwork(raw);
                                                if (paperKind) writePaper(paperKind, raw.trim().toUpperCase());
                                            }}
                                            onBlur={() => setEditing(null)}
                                            onKeyDown={e => { if (e.key === 'Enter') setEditing(null); }}
                                        />
                                    ) : (
                                        <div className="ci-rev-row">
                                            <div className={'ci-rev-v' + (paperwork.trim() ? '' : ' is-none')}>
                                                {paperwork.trim().toUpperCase() || <E k="notGiven" />}
                                            </div>
                                            <button className="ci-rev-edit-b" onClick={() => setEditing('paper')}>
                                                <E k="edit" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {asksPallets && (
                                <div className="ci-rev">
                                    <div className="ci-rev-l"><E k="revPallets" /></div>
                                    {editing === 'pallets' ? (
                                        <input
                                            className="ci-rev-edit"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={4}
                                            autoFocus
                                            value={pallets}
                                            /* ⛔ THE SAME DIGITS-ONLY FILTER as the pallet
                                               screen. A second box onto one field that
                                               accepted more than the first would make the
                                               refusal depend on which screen he typed in. */
                                            onChange={e => setPallets(e.target.value.replace(/[^0-9]/g, ''))}
                                            onBlur={() => setEditing(null)}
                                            onKeyDown={e => { if (e.key === 'Enter') setEditing(null); }}
                                        />
                                    ) : (
                                        <div className="ci-rev-row">
                                            <div className={'ci-rev-v' + (pallets.trim() ? '' : ' is-none')}>
                                                {pallets.trim() || <E k="notGiven" />}
                                            </div>
                                            <button className="ci-rev-edit-b" onClick={() => setEditing('pallets')}>
                                                <E k="edit" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="ci-row">
                            {/* ⛔ BACK GOES TO THE LAST SCREEN THAT EXISTS AT THIS
                                SITE, which is what `prevBefore` is for. */}
                            <button
                                className="ci-secondary"
                                onClick={() => setStep(prevBefore('review'))}
                            ><E k="back" /></button>
                            <button
                                className="ci-primary"
                                disabled={submitting}
                                onClick={() => {
                                    if (config.requireFoodSafetyAck) setStep('safety');
                                    else void submit();
                                }}
                            >{submitting ? <E k="working" /> : <E k="confirmIn" />}</button>
                        </div>
                    </>
                )}

                {step === 'safety' && (
                    <>
                        <div className="ci-h1"><E k="safetyQ" /></div>
                        {/* The wording and its version come from config, so revising it
                            is a screen edit. The version travels with the submission and
                            the server refuses a mismatch. */}
                        <div className="ci-safety">{config.foodSafetyText}</div>
                        <label className="ci-agree">
                            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                            <span><E k="agree" /></span>
                        </label>
                        <div className="ci-row">
                            {/* ⛔ BACK GOES WHERE FORWARD CAME FROM. Hardcoding 'phone'
                                would skip the vehicle step on the way back and a driver
                                correcting a trailer number would have no route to it. */}
                            {/* ⛔ BACK GOES WHERE FORWARD CAME FROM, and the chain
                                knows where that is. Hardcoding 'vehicle' would skip
                                the review screen on the way back — and skip past
                                paperwork and pallets with it. */}
                            <button className="ci-secondary"
                                    onClick={() => setStep(stepIsAsked('review') ? 'review' : 'phone')}><E k="back" /></button>
                            <button
                                className="ci-primary"
                                disabled={submitting || (config.requireFoodSafetyAck && !agreed)}
                                onClick={() => void submit()}
                            >{submitting ? <E k="working" /> : <E k="checkIn" />}</button>
                        </div>
                    </>
                )}

                {step === 'done' && result && (
                    <>
                        {result.status === 'already-checked-in' ? (
                            <>
                                <div className="ci-h1"><E k="already" /></div>
                                <div className="ci-sub">
                                    <E k="againIn" /> {result.minutesAgo} <E k="minsAgo" />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="ci-h1"><E k="doneT" /></div>
                                {/* ⭐ TRUCK NUMBER LEADS, ARRIVAL NUMBER IS A RECEIPT.
                                    The truck number is the one a driver is called by --
                                    "Traffix truck 2" -- because it is per client and it is
                                    what the dock says over the radio. The arrival number is
                                    a facility-wide gate sequence: useful to us, meaningless
                                    to him, and actively misleading if he reads it as a
                                    queue position. Neither number is a place in line, which
                                    is why that is now said out loud. */}
                                {result.truckNumber != null ? (
                                    <>
                                        <div className="ci-big">#{result.truckNumber}</div>
                                        <div className="ci-big-l">
                                            {/* ⚠️ NO CLIENT NAME ANY MORE. It came from the
                                                load the driver picked, and he does not pick
                                                one — a truck number without a client is the
                                                honest version of what we know at the gate. */}
                                            <E k="truck" />
                                        </div>
                                    </>
                                ) : result.arrivalNumber != null && (
                                    <>
                                        <div className="ci-big">#{result.arrivalNumber}</div>
                                        <div className="ci-big-l"><E k="arrival" /></div>
                                    </>
                                )}
                                {result.truckNumber != null && result.arrivalNumber != null && (
                                    <div className="ci-receipt">
                                        <E k="arrival" /> #{result.arrivalNumber}
                                    </div>
                                )}
                                <div className="ci-caveat"><E k="notPlace" /></div>
                                {/**
                                  * ---- ⭐⭐ THE VEHICLE RECEIPT (Pete, 2026-09-23) -----
                                  *
                                  * > *"It goes to the last page that says goto you are
                                  * > checked in screen. If 123 is entered, then it
                                  * > bypasses the confirmation screen of the
                                  * > container/license/trailer number."*
                                  *
                                  * ⛔⛔ THE SERVER SAYS WHETHER THERE IS ANYTHING TO
                                  * CONFIRM, AND THAT IS NOT A DETAIL. This tablet is never
                                  * told the bypass code — `toKioskView` sends the minimum
                                  * LENGTH and withholds the code, because the payload is
                                  * unauthenticated (arch §1D). A screen echoing what the
                                  * driver typed would cheerfully print "Container 123" to
                                  * the one man who told us he had nothing.
                                  *   ** THE SCREEN SHOULD CONFIRM WHAT WAS STORED, NOT
                                  *      WHAT WAS TYPED. ONLY ONE OF THOSE IS A FACT. **
                                  *
                                  * ⚠️ NULL OR ABSENT MEANS SAY NOTHING, in four
                                  * situations that all deserve silence: the site does not
                                  * ask, he typed the bypass code, he gave nothing, or the
                                  * server is older than this field.
                                  */}
                                {result.vehicleRecorded && (
                                    <div className="ci-recorded">
                                        <div className="ci-recorded-l">
                                            <E k={result.vehicleRecorded.kind === 'container' ? 'recContainer'
                                                : result.vehicleRecorded.kind === 'trailer' ? 'recTrailer'
                                                : 'recPlate'} />
                                        </div>
                                        <div className="ci-recorded-v">{result.vehicleRecorded.value}</div>
                                    </div>
                                )}
                                {/* ⚠️ Deliberately vague. The response carries no appointment
                                    numbers even when several matched -- telling a driver which
                                    bookings exist would hand anyone on the facility wifi a list
                                    of the day's appointments. "See the office" is the whole message. */}
                                {/* Told to every checked-in driver, matched or not: the
                                    numbers above are a receipt, not a position in a queue,
                                    and somebody will come and find him. */}
                                {!result.needsOffice && (
                                    <div className="ci-next"><E k="willTell" /></div>
                                )}
                                {result.needsOffice && (
                                    <div className="ci-office">
                                        <div className="ci-h2"><E k="office" /></div>
                                        <div className="ci-sub"><E k="officeWhy" /></div>
                                    </div>
                                )}
                            </>
                        )}
                        <button className="ci-primary" onClick={reset}>
                            <E k="another" />
                        </button>
                        <div className="ci-hint">
                            <E k="resetting" /> {doneLeft}s
                        </div>
                    </>
                )}

                {step !== 'done' && step !== 'phone' && (
                    <button className="ci-cancel" onClick={reset}><E k="cancel" /></button>
                )}
            </div>
            {busyMsg && <div className="ci-sub">{busyMsg}</div>}
        </div>
    );
}

/* Self-contained, like DockBoard's BOARD_CSS: a gate tablet is a fixed device
   and this page owes nothing to the app's layout system. Large targets, high
   contrast. ⚠️ `.es` SURVIVES BELOW and is currently emitted by nothing -
   `KIOSK_SHOW_SPANISH` is false. Kept so turning the flag back on is one
   edit rather than one edit plus a stylesheet nobody remembers. */
const CSS = `
/* ==========================================================================
   FLUID TYPE AND READABLE DISABLED STATE (Pete, 2026-09-25)

   Pete: "the laborclock screens render great on a cell phone but the driver
   checkin does not - thought we built them the same on rendering across
   devices?"

   !! THEY WERE NEVER BUILT THE SAME, AND THAT IS THE WHOLE ANSWER. Counted
   before changing anything:

                            kiosk        this file
       clamp() uses           63             1
       vw units               52             1
       width breakpoints       3             0

   The kiosk is fluid by construction. This file was a fixed-pixel desktop
   layout whose only clamp was on the locked-out red X - and the comment
   beside it even says "clamp() so it holds on a phone-sized" screen. Someone
   knew the technique and reached for it exactly once.

   !! THE CARD ITSELF WAS ALREADY FINE. It is width:100% with max-width:560px
   and it reflows correctly. What did not reflow was the TYPE INSIDE it: a
   34px heading plus 48px of combined side padding on a 375px phone leaves
   almost nothing for the words.
     ** A CONTAINER THAT RESIZES IS NOT A RESPONSIVE LAYOUT IF EVERYTHING
        INSIDE IT IS MEASURED IN PIXELS. **

   ! AND THE TWO MEDIA QUERIES BELOW ARE NOT BREAKPOINTS. Both are
   (hover: hover) - a question about the POINTER, not the screen. There is
   still not one width breakpoint in this file, and after this change there
   does not need to be.

   ---- THE DISABLED STATE, MEASURED --------------------------------------

   Pete: "I think we are making them lighter because they are suppose to be
   greyed out or not selectable but that is a tough way to see."

   He was right about the cause and it was worse than it looked. Computed
   against the surfaces these actually sit on:

       .ci-label em     #64748b on #1e293b ....  3.07:1   fails AA
       .ci-primary:disabled  #64748b on #334155  2.18:1   fails badly

   The first is ordinary body text - the "(optional)" hint beside a field
   label - and it has never been legible. Both are now lifted.

   ** DIMMING TEXT TO MEAN UNAVAILABLE SPENDS THE ONE CHANNEL THAT CARRIES
      THE WORDS. ** Unavailability is already said by the muted background
   and the not-allowed cursor; the text no longer has to be unreadable to
   repeat it.
   ========================================================================== */
.ci-root { position:fixed; inset:0; background:#0f172a; color:#f8fafc;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:clamp(12px,3.5vw,24px); overflow-y:auto; }
.ci-brand { letter-spacing:.35em; font-size:clamp(12px,3.2vw,15px); color:#94a3b8; margin-bottom:clamp(10px,3vw,18px); }
.ci-card { width:100%; max-width:560px; background:#1e293b; border-radius:16px;
  padding:clamp(16px,4vw,28px) clamp(12px,3.5vw,24px); box-shadow:0 20px 60px rgba(0,0,0,.45); }
.ci-h1 { font-size:clamp(24px,7vw,34px); font-weight:700; text-align:center; line-height:1.15; }
.ci-h2 { font-size:clamp(19px,5.2vw,25px); font-weight:700; text-align:center; }
.ci-sub { text-align:center; color:#94a3b8; margin-top:8px; font-size:clamp(15px,4.2vw,20px); }
/* ⭐ THE TEXTING NOTICE (2026-08-27). Smaller and dimmer than .ci-sub on purpose:
   it is a disclosure, not an instruction, and it must not compete with the
   question the driver is being asked. ⚠️ But still comfortably readable on a
   tablet at arm's length — a notice nobody can read is not a notice. */
.ci-note { text-align:center; color:#94a3b8; margin:10px auto 0; font-size:clamp(12px,3.2vw,15px);
           line-height:1.45; max-width:34em; opacity:.85; }
.es { display:block; font-size:.66em; font-weight:400; color:#94a3b8; margin-top:2px; }
.ci-phone { text-align:center; font-size:clamp(26px,8vw,38px); font-weight:700; letter-spacing:.04em;
  margin:22px 0 14px; min-height:46px; font-variant-numeric:tabular-nums; }
.ci-pad { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.ci-key { height:clamp(52px,13vw,66px); font-size:clamp(20px,5.5vw,26px); font-weight:600; border-radius:12px;
  border:1px solid #334155; background:#0f172a; color:#f8fafc; cursor:pointer; }
.ci-key:active { background:#334155; }
.ci-key-blank { visibility:hidden; }
.ci-primary { width:100%; margin-top:18px; padding:clamp(14px,3.6vw,20px); font-size:clamp(18px,4.8vw,23px); font-weight:700;
  border:0; border-radius:12px; background:#2563eb; color:#fff; cursor:pointer; }
.ci-primary:disabled { background:#334155; color:#a8b6c8; cursor:not-allowed; }
.ci-secondary { flex:1; margin-top:18px; padding:clamp(14px,3.6vw,20px); font-size:clamp(17px,4.4vw,21px); font-weight:600;
  border:1px solid #334155; border-radius:12px; background:transparent; color:#cbd5e1; cursor:pointer; }
/* ⚠️ SAME TOUCH RULE as the classify buttons - Back and Skip sit in the same
   place across three screens, which is exactly where a stuck hover is visible. */
@media (hover: hover) {
  .ci-secondary:hover { background:#334155; border-color:#475569; color:#f8fafc; }
  .ci-primary:hover:not(:disabled) { background:#1d4ed8; }
}
.ci-key-clear { color:#fca5a5; font-size:clamp(20px,5.5vw,26px); }
.ci-row { display:flex; gap:12px; }
.ci-row .ci-primary { flex:2; }
.ci-field { display:block; margin-top:16px; }
/* ⭐ 24px (Pete, 2026-09-23): *"alittle bigger on text size but smaller than
   Your Trailer or Container text size"* - the .ci-h1 above it is 34px, and the
   value he types is 24px, so the question and the answer now read as one unit. */
.ci-label { display:block; font-size:clamp(18px,4.8vw,24px); color:#e2e8f0; margin-bottom:8px; }
.ci-label em { color:#94a3b8; font-style:normal; font-size:.85em; }
.ci-field input, .ci-field select { width:100%; padding:clamp(11px,3vw,16px); font-size:clamp(18px,4.8vw,24px); border-radius:10px;
  border:1px solid #334155; background:#0f172a; color:#f8fafc; }
/* ⭐ *"should be lit up and bordered with the cursor on it immediately."* The
   box is autoFocused, so this is what "immediately" looks like. ⚠️ outline is
   NOT set to none - the ring is ADDED to the browser's own focus indicator
   rather than replacing it, because a kiosk is still a keyboard-reachable page. */
.ci-field input:focus { border-color:#60a5fa; box-shadow:0 0 0 3px rgba(96,165,250,.35);
  outline-offset:2px; }
.ci-field select { appearance:none; cursor:pointer; }
.ci-safety { margin:16px 0; padding:16px; border-radius:10px; background:#0f172a;
  border:1px solid #334155; font-size:clamp(15px,4vw,19px); line-height:1.5; max-height:34vh; overflow-y:auto; }
.ci-agree { display:flex; align-items:center; gap:12px; font-size:clamp(17px,4.5vw,22px); font-weight:600;
  padding:14px; border-radius:10px; background:#0f172a; border:1px solid #334155; cursor:pointer; }
.ci-agree input { width:26px; height:26px; }
.ci-nums { display:flex; gap:16px; justify-content:center; margin:22px 0; }
.ci-num { flex:1; text-align:center; background:#0f172a; border-radius:12px; padding:16px 8px; }
.ci-num-v { font-size:clamp(28px,8.5vw,40px); font-weight:800; font-variant-numeric:tabular-nums; }
.ci-num-l { font-size:clamp(12px,3.2vw,15px); color:#94a3b8; margin-top:4px; }
.ci-matches { display:flex; flex-direction:column; gap:12px; margin:20px 0 4px; }
.ci-match { text-align:left; padding:16px; border-radius:12px; cursor:pointer;
  border:1px solid #334155; background:#0f172a; color:#f8fafc; }
.ci-match:hover { border-color:#60a5fa; background:#15243f; }
.ci-match:active { background:#1d2f52; }
.ci-match.is-on { border-color:#2563eb; box-shadow:0 0 0 2px rgba(37,99,235,.45); }
.ci-match-rows { margin-top:10px; display:flex; flex-direction:column; gap:3px;
  font-size:clamp(11px,2.9vw,14px); color:#e2e8f0; }
.ci-match-rows span { display:inline-block; min-width:78px; color:#94a3b8; }
.ci-match-no { font-size:clamp(19px,5vw,24px); font-weight:800; letter-spacing:.02em; }
.ci-match-when { font-size:clamp(14px,3.6vw,17px); color:#cbd5e1; margin-top:4px; }
.ci-match-door { font-size:clamp(12px,3.2vw,14px); color:#94a3b8; margin-top:6px; }
.ci-big { text-align:center; font-size:clamp(48px,16vw,76px); font-weight:800; line-height:1;
  margin:18px 0 4px; font-variant-numeric:tabular-nums; }
.ci-big-l { text-align:center; font-size:clamp(16px,4.4vw,21px); color:#cbd5e1; font-weight:600; }
.ci-receipt { text-align:center; margin-top:14px; font-size:clamp(12px,3.4vw,16px); color:#94a3b8; }
.ci-caveat { text-align:center; margin-top:6px; font-size:clamp(11px,3.1vw,15px); color:#64748b;
  font-style:italic; }
.ci-choice { flex:1; margin-top:0; padding:18px; font-size:clamp(14px,3.8vw,18px); font-weight:700;
  border:1px solid #334155; border-radius:12px; background:#0f172a; color:#cbd5e1;
  cursor:pointer; }
.ci-choice:hover { background:#1e293b; }
.ci-choice { display:flex; align-items:center; justify-content:center; gap:8px; }
/* ⭐ CHOSEN, NOT CLICKABLE. A lighter fill plus a ring reads as "this is set";
   the solid primary blue is reserved for Next, which is the thing to press. */
.ci-choice.is-on { background:#1d3f7a; border-color:#60a5fa; color:#fff;
                   box-shadow:inset 0 0 0 2px #60a5fa; }
/* ⚠️ The slot is always there so the label does not jump when a tick appears. */
.ci-tick { display:inline-block; min-width:1em; font-size:clamp(15px,4.2vw,20px); line-height:1;
           color:#93c5fd; font-weight:700; }
.ci-dir { margin-top:6px; }
.ci-next { margin-top:8px; padding:14px; border-radius:12px; background:#0f172a;
  border:1px solid #334155; font-size:clamp(14px,3.8vw,18px); color:#cbd5e1; text-align:center; }
.ci-office { margin-top:8px; padding:16px; border-radius:12px;
  background:#78350f; border:1px solid #b45309; }
.ci-office .ci-sub { color:#fde68a; }
.ci-error { margin-bottom:14px; padding:12px; border-radius:10px;
  background:#7f1d1d; border:1px solid #b91c1c; font-size:clamp(14px,4vw,19px); text-align:center; }
.ci-hint { margin-top:12px; text-align:center; color:#94a3b8; font-size:clamp(14px,3.8vw,18px); }
.ci-help { display:block; margin-top:6px; font-size:clamp(11px,3.1vw,15px); color:#64748b; }
.ci-wide { width:100%; }
.ci-cancel { width:100%; margin-top:16px; padding:20px; font-size:clamp(16px,4.4vw,21px); font-weight:600;
  border:1px solid #475569; border-radius:12px; background:transparent; color:#f8fafc;
  cursor:pointer; }
.ci-cancel:hover { background:#334155; }
/* ⭐ THE REVIEW LIST. Quiet rows, loud values — the value is the thing he is
   being asked to check, and the label is only there to say which question it
   answers. ⚠️ The is-none variant is dimmed and italic rather than red: "Not
   given" is a permitted answer on the pallet screen, not an error.
   ⛔ NO BACKTICKS ANYWHERE IN THIS BLOCK. It lives inside a JS template literal;
   one backtick ends the stylesheet and the parse error lands far away. Cost an
   hour once already today, in a SQL template literal, same evening. */
.ci-rev-list { margin-top:18px; display:flex; flex-direction:column; gap:10px; }
.ci-rev { padding:14px 16px; border-radius:10px; background:#0f172a;
  border:1px solid #334155; }
.ci-rev-l { font-size:clamp(11px,3.1vw,15px); color:#94a3b8; }
.ci-rev-v { margin-top:4px; font-size:clamp(18px,5vw,24px); font-weight:800; letter-spacing:.03em;
  word-break:break-all; }
.ci-rev-v.is-none { font-size:clamp(14px,4vw,19px); font-weight:600; font-style:italic; color:#64748b;
  letter-spacing:normal; }
.ci-locked { text-align:center; border:1px solid #ef4444; }
/* ⭐ THE SAME RED THE CLOCK USES, and the same proportions: a glyph about 2.5x the
   heading, sitting directly above it. ⚠️ clamp() so it holds on a phone-sized
   preview as well as a gate tablet. */
.ci-locked-x { font-size:clamp(48px,16vw,88px); line-height:1; color:#ef4444; margin-bottom:8px; }
/* ---- ⭐⭐ THE ONE-BOX VEHICLE STEP (2026-09-23) ------------------------
   ⚠️ The three buttons STACK rather than sit in a row. Their labels are whole
   sentences in two languages ("This is my CONTAINER number" over "Este es mi
   número de CONTENEDOR"), and three of those side by side on a tablet is three
   columns of wrapped text nobody reads at a gate in the rain.
   ⛔ FULL WIDTH AND TALL, because each one is a SUBMIT on both live sites. A
   mis-tap here files a wrong number against a truck. */
.ci-which { margin-top:22px; text-align:center; font-size:clamp(14px,4vw,19px); font-weight:600;
  color:#cbd5e1; }
.ci-classify { display:flex; flex-direction:column; gap:12px; margin-top:12px; }
/* ---- ⭐⭐ ONE BUTTON COLOUR (Pete, 2026-09-23) ------------------------------
   > *"buttons need to be consistent. On the second screen they are blue
   > background, white lettering, needs to be consistent!"*

   ⛔ THE VEHICLE AND PAPERWORK SCREENS ALREADY SHARED THIS CLASS, so they could
   not differ by markup - which means what Pete saw was the HOVER state stuck on
   after a tap (see the media query below). Both halves are fixed: the resting
   color now matches .ci-primary, and hover can no longer stick.
     ** WHEN TWO THINGS THAT SHARE A CLASS LOOK DIFFERENT, THE DIFFERENCE IS
        STATE, NOT STYLE. ** */
.ci-classify-b { width:100%; padding:20px; font-size:clamp(16px,4.4vw,21px); font-weight:700;
  border:0; border-radius:12px; background:#2563eb; color:#fff;
  cursor:pointer; text-align:center; line-height:1.25; }
.ci-classify-b:active:not(:disabled) { background:#1d4ed8; }
.ci-classify-b:disabled { background:#334155; color:#64748b; cursor:not-allowed; }

/* ---- ⛔⛤ HOVER STICKS ON A TOUCHSCREEN, AND THIS IS A TOUCHSCREEN -----------

   A tap on a touch device leaves :hover applied to the element until something
   else is tapped. The classify buttons sit in the SAME PLACE on the vehicle and
   paperwork screens, so tapping one on screen 1 left its twin on screen 2 wearing
   the hover color before the driver had touched it.
     ** :hover ON A DEVICE WITH NO POINTER IS NOT A HOVER. IT IS A STICKY
        SELECTION NOBODY ASKED FOR. **
   !! NO BACKTICKS ANYWHERE IN THIS BLOCK. It is inside the CSS template
      literal, and a backtick in a comment inside one is not a
      comment - it is the end of the string. Caught by the TS parse check on
      2026-09-23, for the third time in this file's life.
   ⚠️ The :active rule IS DELIBERATELY OUTSIDE THIS QUERY - it lasts only while the
   finger is down, which is exactly the touch feedback a tablet wants. */
@media (hover: hover) {
  .ci-classify-b:hover:not(:disabled) { background:#1d4ed8; }
}

/* ⭐ THE REVIEW ROW'S EDIT (2026-09-23). ⚠️ The value and the control share a
   line; the label above them already names the field. */
.ci-rev-row { display:flex; align-items:center; gap:10px; }
.ci-rev-row .ci-rev-v { flex:1; min-width:0; }
.ci-rev-edit-b { flex:0 0 auto; padding:10px 16px; font-size:clamp(12px,3.4vw,16px); font-weight:600;
  border:1px solid #334155; border-radius:10px; background:transparent;
  color:#cbd5e1; cursor:pointer; }
.ci-rev-edit { width:100%; margin-top:4px; padding:12px; font-size:clamp(18px,5vw,24px); font-weight:800;
  letter-spacing:.03em; border-radius:10px; border:1px solid #60a5fa;
  background:#020617; color:#f8fafc;
  box-shadow:0 0 0 3px rgba(96,165,250,.35); }
.ci-classify-b:disabled .es { color:#475569; }
/* ⭐ THE RECEIPT ON THE DONE SCREEN. Quiet — the truck number above it is what
   the driver is waiting for; this is a "yes, we wrote it down". */
.ci-recorded { margin-top:14px; padding:12px; border-radius:10px; background:#0f172a;
  border:1px solid #334155; text-align:center; }
.ci-recorded-l { font-size:clamp(11px,3.1vw,15px); color:#94a3b8; }
.ci-recorded-v { margin-top:4px; font-size:clamp(20px,5.5vw,26px); font-weight:800; letter-spacing:.04em;
  font-variant-numeric:tabular-nums; }
`;
